<template>
  <div class="container">
    <SimpleLoader v-if="isLoading" />
    <div v-if="!isLoading">
      
      <!-- Top-level stepper (Select Client -> Proposal Details) -->
      <div v-if="isIndependentProposal" class="d-flex justify-content-center align-items-center mb-4 px-4 py-2" style="background-color: #f8f9fa; border-radius: 12px; border: 1px solid #e9ecef; user-select: none;">
        <div class="step-item d-flex align-items-center" :class="{ 'text-info fw-bold': !lead, 'text-muted': lead }" :style="lead ? 'cursor: pointer;' : ''" @click="lead ? goToStep1() : null">
           <div class="step-circle" :style="!lead ? 'background-color: #00bcd4; color: white;' : 'background-color: #e9ecef; color: #6c757d;'" style="width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold;">1</div>
           <div class="step-label ms-2" style="font-size: 14px;">Select Client</div>
        </div>
        <div style="width: 50px; height: 2px; background-color: #dee2e6; margin: 0 15px;"></div>
        <div class="step-item d-flex align-items-center" :class="{ 'text-info fw-bold': lead, 'text-muted': !lead }">
           <div class="step-circle" :style="lead ? 'background-color: #00bcd4; color: white;' : 'background-color: #e9ecef; color: #6c757d;'" style="width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold;">2</div>
           <div class="step-label ms-2" style="font-size: 14px;">Proposal Details</div>
        </div>
      </div>
      
      <!-- Step 1: Lead Setup if lead is null -->
      <div v-if="!lead" class="lead-setup-container text-start" style="color: #333; padding: 0.5rem 0;">
        
        <!-- Mode A: Existing Lead -->
        <div v-if="leadSelectionMode === 'existing'">
          <div class="mb-3">
            <label class="form-label fw-bold">Select Existing Client</label>
            <VueMultiselect
              v-model="selectedLead"
              :options="leadsList"
              :searchable="true"
              :close-on-select="true"
              :show-labels="false"
              placeholder="Search by organisation name, contact email or name..."
              label="displayName"
              track-by="uniqueKey"
            >
            </VueMultiselect>
          </div>

          <!-- Contact Selection Dropdown for Auth-linked Organisation -->
          <div v-if="selectedLead && isSelectedLeadAuthLinked" class="mb-3 animate__animated animate__fadeIn">
            <label class="form-label fw-bold d-flex align-items-center justify-content-between mb-2">
              <span>Proposal Contact Person <span class="text-danger">*</span></span>
              <span v-if="isLoadingContacts" class="text-muted small fw-normal d-inline-flex align-items-center gap-2">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span>Loading organisation contacts...</span>
              </span>
            </label>
            <VueMultiselect
              v-model="selectedContact"
              :options="orgContacts"
              :searchable="true"
              :close-on-select="true"
              :show-labels="false"
              select-label=""
              deselect-label=""
              selected-label=""
              placeholder="Search and select contact from organisation..."
              label="displayName"
              track-by="key"
              :disabled="isLoadingContacts"
              class="contact-multiselect"
            >
              <template #singleLabel="{ option }">
                <div class="d-flex align-items-center gap-2 text-truncate" style="line-height: normal;">
                  <span class="fw-bold">{{ option.fullName }}</span>
                  <span v-if="option.email" class="text-muted small">({{ option.email }})</span>
                  <span
                    v-if="option.contact_type"
                    class="badge rounded-pill text-uppercase px-2 py-0.5 ms-1 contact-badge-item"
                    :class="getContactTypeBadgeClass(option.contact_type)"
                    style="font-size: 10px; font-weight: 700; letter-spacing: 0.3px;"
                  >
                    {{ option.contact_type }}
                  </span>
                  <span
                    v-if="option.is_primary && (!option.contact_type || option.contact_type.toLowerCase() !== 'primary')"
                    class="badge rounded-pill px-1.5 py-0.5 ms-1 contact-badge-item contact-type-badge-primary"
                    style="font-size: 9.5px; font-weight: 600;"
                  >
                    ★ Primary
                  </span>
                </div>
              </template>
              <template #option="{ option }">
                <div class="d-flex align-items-center justify-content-between w-100 py-0.5" style="gap: 12px;">
                  <div class="d-flex align-items-center gap-1 text-truncate" style="min-width: 0; flex: 1 1 auto;">
                    <span class="contact-option-name fw-bold">{{ option.fullName }}</span>
                    <span v-if="option.email" class="contact-option-sub text-muted small ms-1">({{ option.email }})</span>
                    <span v-if="option.role" class="contact-option-sub text-muted small ms-1">- {{ option.role }}</span>
                  </div>
                  <div class="d-flex align-items-center gap-1.5 flex-shrink-0" style="margin-left: auto;">
                    <span
                      v-if="option.contact_type"
                      class="badge rounded-pill text-uppercase px-2 py-0.5 contact-badge-item"
                      :class="getContactTypeBadgeClass(option.contact_type)"
                      style="font-size: 10px; font-weight: 700; letter-spacing: 0.3px;"
                    >
                      {{ option.contact_type }}
                    </span>
                    <span
                      v-if="option.is_primary && (!option.contact_type || option.contact_type.toLowerCase() !== 'primary')"
                      class="badge rounded-pill px-1.5 py-0.5 contact-badge-item contact-type-badge-primary"
                      style="font-size: 9.5px; font-weight: 600;"
                      title="Primary Contact"
                    >
                      ★ Primary
                    </span>
                  </div>
                </div>
              </template>
              <template #noResult>No matching contacts found.</template>
              <template #noOptions>No contacts found for this organisation.</template>
            </VueMultiselect>
          </div>

          <div v-if="selectedLead" class="lead-preview-card p-3 p-md-4 mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
              <h6 class="fw-bold mb-0 text-dark" style="font-size: 15px; letter-spacing: -0.2px;">Client Information</h6>
              <span v-if="selectedLead.is_charity" class="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2.5 py-1 d-inline-flex align-items-center gap-1.5" style="font-size: 11px; font-weight: 600;">
                <i class="fa-solid fa-heart" style="font-size: 10px;"></i> <span>Charity</span>
              </span>
            </div>

            <div class="row g-3">
              <!-- Organisation -->
              <div class="col-md-6">
                <div class="client-info-label">ORGANISATION</div>
                <div class="client-info-value fw-bold text-dark">
                  {{ selectedLeadOrgName }}
                  <span v-if="selectedLeadAbn" class="text-muted small fw-normal ms-1">(ABN: {{ selectedLeadAbn }})</span>
                </div>
              </div>

              <!-- Contact Person -->
              <div class="col-md-6">
                <div class="client-info-label">CONTACT PERSON</div>
                <div class="client-info-value fw-bold text-dark">
                  {{ activeContactName || 'Not Provided' }}
                </div>
              </div>

              <!-- Email -->
              <div class="col-md-6">
                <div class="client-info-label">EMAIL ADDRESS</div>
                <div class="client-info-value">
                  {{ activeContactEmail || 'Not Provided' }}
                </div>
              </div>

              <!-- Phone -->
              <div class="col-md-6">
                <div class="client-info-label">PHONE NUMBER</div>
                <div class="client-info-value">
                  {{ activeContactPhone || 'Not Provided' }}
                </div>
              </div>

              <!-- Country -->
              <div class="col-md-6">
                <div class="client-info-label">COUNTRY</div>
                <div class="client-info-value">
                  {{ selectedLeadCountry }}
                </div>
              </div>

              <!-- Charity Status -->
              <div class="col-md-6">
                <div class="client-info-label">CHARITY STATUS</div>
                <div class="client-info-value">
                  {{ selectedLead.is_charity ? 'Registered Charity' : 'Standard (No)' }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedLead && selectedLeadHasNoBdm" class="alert alert-warning text-start small mb-4 mt-2 animate__animated animate__shakeX" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e; border-left: 4px solid #fbbf24; border-radius: 8px; padding: 18px 20px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
            <div class="d-flex align-items-start">
              <i class="fa-solid fa-triangle-exclamation me-3 mt-1" style="font-size: 1.25rem; color: #f59e0b;"></i>
              <div style="flex-grow: 1;">
                <strong style="font-size: 14px; display: block; margin-bottom: 4px;">BDM Assignment Required</strong>
                <p class="mb-3" style="font-size: 13px; color: #b45309; line-height: 1.4;">This client does not have a BDM assigned to it. You can select and assign a BDM directly below to proceed instantly:</p>
                
                <div class="d-flex align-items-center gap-2 flex-wrap" style="max-width: 480px;">
                  <VueMultiselect
                    v-model="selectedInlineBdm"
                    :options="bdmOptions"
                    :multiple="false"
                    :close-on-select="true"
                    :searchable="true"
                    placeholder="-- Select BDM to Assign --"
                    label="displayName"
                    track-by="db_id"
                    open-direction="bottom"
                    select-label=""
                    deselect-label=""
                    selected-label=""
                    class="inline-bdm-multiselect"
                  >
                  </VueMultiselect>
                  <button @click.prevent="assignBdmInline" :disabled="isSavingInlineBdm || !selectedInlineBdmId" class="btn btn-sm text-white px-3 fw-bold d-inline-flex align-items-center gap-2" :style="'background-color: ' + (isSavingInlineBdm || !selectedInlineBdmId ? '#80d0df' : '#00A0C1') + ' !important; border-color: ' + (isSavingInlineBdm || !selectedInlineBdmId ? '#80d0df' : '#00A0C1') + ' !important; opacity: 1 !important; height: 36px; font-size: 13px; border-radius: 18px; transition: all 0.2s ease;'">
                    <i v-if="isSavingInlineBdm" class="fa-solid fa-spinner fa-spin"></i>
                    <i v-else class="fa-solid fa-check"></i>
                    Assign BDM
                  </button>
                </div>
              </div>
            </div>
          </div>

           <div v-if="isSelectedLeadMissingContactInfo" class="alert alert-warning text-start small mb-4 mt-2" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e; border-left: 4px solid #f59e0b; border-radius: 8px; padding: 15px;">
            <div class="d-flex align-items-start">
              <i class="fa-solid fa-circle-exclamation me-2 mt-1" style="font-size: 16px;"></i>
              <div>
                <strong style="font-size: 14px;">Incomplete Lead Contact Details</strong>
                <p class="mb-0 mt-1" style="font-size: 13px;">This client is maintained in Auth but is missing a valid first name, last name or email address. Please update their details in Auth, before syncing in Spark to continue with proposal creation.</p>
                <div class="mt-3">
                  <button @click.prevent="openLeadInAuth(selectedLead)" class="btn btn-sm text-white px-3 py-1.5 fw-bold" style="background-color: #00A0C1 !important; border-color: #00A0C1 !important; border-radius: 4px; font-size: 12px; display: inline-flex; align-items: center; gap: 6px;">
                    <i class="fa-solid fa-arrow-up-right-from-square"></i> Open Organisation in Auth
                  </button>
                </div>
              </div>
            </div>
          </div>

           <div v-if="selectedLead && selectedLeadCannotCreateProposal" class="alert alert-warning text-start small mb-4 mt-2" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e; border-left: 4px solid #ef4444; border-radius: 8px; padding: 15px;">
            <div class="d-flex align-items-start">
              <i class="fa-solid fa-ban me-2 mt-1" style="font-size: 16px; color: #ef4444;"></i>
              <div>
                <strong style="font-size: 14px; color: #dc2626;">Auth Sync Required for Additional Proposals</strong>
                <p class="mb-0 mt-1" style="font-size: 13px;">This Client organisation is not fully synced with Auth. Additional proposals can only be created for Client organisations that are fully synced with Auth.</p>
              </div>
            </div>
          </div>

          <div class="d-flex flex-column align-items-center mt-4">
            <button v-if="!isSelectedLeadMissingContactInfo && !selectedLeadHasNoBdm && !selectedLeadCannotCreateProposal" class="btn text-white px-5 py-2 fw-bold mb-3" :disabled="!selectedLead" style="background-color: #00A0C1 !important; border-color: #00A0C1 !important; border-radius: 20px;" @click="proceedWithExistingLead">
              Proceed to Items Selection <i class="fa-solid fa-arrow-right ms-1"></i>
            </button>
            <div style="font-size: 14px; color: #666;">
              Not finding the client? 
              <a href="#" @click.prevent="leadSelectionMode = 'new'; newLeadStep = 1;" class="text-info fw-bold" style="text-decoration: underline;">
                Create New Lead
              </a>
            </div>
          </div>
        </div>

        <!-- Mode B: New Lead -->
        <div v-if="leadSelectionMode === 'new'">
          <div class="row">
            <!-- Organisation Name -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">Organisation Name <span class="text-danger">*</span></label>
              <select v-if="abnRecordFound && availableOrganisationNames.length" class="form-select" v-model="leadForm.organisationName">
                <option v-for="name in availableOrganisationNames" :key="name" :value="name">{{ name }}</option>
              </select>
              <input v-else type="text" class="form-control" v-model="leadForm.organisationName" @input="handleOrgInput" placeholder="e.g. Acme Corp">
            </div>

            <!-- Country Selection -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">Country <span class="text-danger">*</span></label>
              <VueMultiselect
                v-model="leadForm.country"
                :options="countryOptions"
                :searchable="true"
                :close-on-select="true"
                :show-labels="false"
                placeholder="Select Country"
                label="name"
                track-by="code"
              />
            </div>

            <!-- Business Number -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">{{ leadFormBusinessNumberLabel }} (Optional)</label>
              <input type="text" class="form-control" v-model="leadForm.abn" @keyup="handleAbnInput" :placeholder="leadFormBusinessNumberPlaceholder">
              <p v-if="abnLookup.isLoading" class="form-text text-info mb-0" style="font-size: 12px;">Validating {{ leadFormBusinessNumberShortLabel }}...</p>
              <p v-if="!abnLookup.isLoading && abnLookup.message" class="form-text mb-0" style="font-size: 12px;"
                 :class="abnLookup.status === 'success' ? 'text-success' : 'text-danger'">
                 {{ abnLookup.message }}
              </p>
            </div>

            <!-- Is Charity Checkbox (Styled) -->
            <div class="col-md-6 mb-3 d-flex align-items-center mt-4">
              <label class="checkbox-label mb-0" @click.stop>
                <input type="checkbox" v-model="leadForm.isCharity" class="styled-checkbox">
                <span class="checkbox-custom"></span>
                Is a registered charity?
              </label>
            </div>

            <!-- Contact Details -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">Contact First Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model="leadForm.firstName" placeholder="John">
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">Contact Last Name</label>
              <input type="text" class="form-control" v-model="leadForm.lastName" placeholder="Doe (Optional)">
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">Contact Email <span class="text-danger">*</span></label>
              <input type="email" class="form-control" v-model="leadForm.email" placeholder="john.doe@example.com">
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">Contact Phone Number <span class="text-danger">*</span></label>
              <input type="tel" class="form-control" v-model="leadForm.phoneNumber" placeholder="+61 412 345 678">
            </div>

            <!-- Lead Type & Priority -->
            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">Lead Type <span class="text-danger">*</span></label>
              <VueMultiselect id="leadType" v-model="selectedProposalLeadType" :options="leadTypes"
                :multiple="false" :close-on-select="true" :searchable="false"
                placeholder="Select Type" label="display_name" track-by="id"
                select-label="" deselect-label="" selected-label="">
              </VueMultiselect>
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label fw-bold">Lead Priority <span class="text-danger">*</span></label>
              <VueMultiselect id="priority" v-model="selectedProposalPriority" :options="leadPriorityTypes"
                :multiple="false" :close-on-select="true" :searchable="false"
                placeholder="Select Priority" label="display_name" track-by="id"
                select-label="" deselect-label="" selected-label="">
                <template #option="{ option }">
                  <span :class="priorityClass(option.id)">{{ option.display_name }}</span>
                </template>
                <template #singleLabel="{ option }">
                  <span :class="priorityClass(option.id)">{{ option.display_name }}</span>
                </template>
              </VueMultiselect>
            </div>

            <!-- Notes -->
            <div class="col-md-12 mb-3">
              <label class="form-label fw-bold">Enquiry Details / Note <span class="text-danger">*</span></label>
              <textarea class="form-control" v-model="leadForm.enquiryDetails" rows="3" placeholder="Additional details or notes about the lead..."></textarea>
            </div>

            <!-- BDM Assignment -->
            <div v-if="$store.state.auth.isConsultant || $store.getters['auth/isGlobalSuperAdmin']" class="col-md-12 mb-3">
              <label class="form-label fw-bold">Assign BDM <span class="text-danger">*</span></label>
              <VueMultiselect id="assigned_user" v-model="selectedProposalBDM" :options="bdmOptions"
                :multiple="false" :close-on-select="true" :searchable="true"
                placeholder="Select a BDM" label="displayName" track-by="db_id"
                open-direction="bottom" select-label="" deselect-label="" selected-label="">
              </VueMultiselect>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-4 w-100 px-3">
              <button class="btn btn-outline-secondary px-4 py-2" @click="resetLeadSelection">
                Cancel
              </button>
              <button class="btn text-white px-4 py-2 fw-bold" style="background-color: #00A0C1 !important; border-color: #00A0C1 !important; border-radius: 4px; border: none !important;" @click="createLeadAndProceed">
                Create Lead & Proceed <i class="fa-solid fa-arrow-right ms-1"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <div v-if="isLeadMissingContactInfo" class="alert alert-warning text-start small mb-4" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e; border-left: 4px solid #f59e0b; border-radius: 8px; padding: 15px;">
          <div class="d-flex align-items-start">
            <i class="fa-solid fa-circle-exclamation me-2 mt-1" style="font-size: 16px;"></i>
            <div>
              <strong style="font-size: 14px;">Incomplete Lead Contact Details</strong>
              <p class="mb-0 mt-1" style="font-size: 13px;">This client is maintained in Auth but is missing a valid first name, last name or email address. Please update their details in Auth, before syncing in Spark to continue with proposal creation.</p>
              <div class="mt-3">
                <button @click.prevent="openLeadInAuth(lead)" class="btn btn-sm text-white px-3 py-1.5 fw-bold" style="background-color: #00A0C1 !important; border-color: #00A0C1 !important; border-radius: 4px; font-size: 12px; display: inline-flex; align-items: center; gap: 6px;">
                  <i class="fa-solid fa-arrow-up-right-from-square"></i> Open Organisation in Auth
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="proposal-header d-flex justify-content-between align-items-center">
          <div>
            <div class="proposal-name-label">Proposal Name</div>
            <div class="proposal-name text-start">
              <span>{{ displayedProposalTitle }}</span>
              <span v-if="selectedContact" class="ms-2 badge bg-light text-dark border" style="font-weight: normal; font-size: 13px;">
                <i class="fa-solid fa-user me-1 text-info"></i> {{ selectedContact.fullName || (selectedContact.first_name + ' ' + selectedContact.last_name).trim() }}
              </span>
              <a v-if="isIndependentProposal" href="#" @click.prevent="resetLeadSelection" class="text-info ms-3" style="font-size: 13px; text-decoration: underline;">
                [Change Client]
              </a>
            </div>
          </div>
        </div>

        <br>

        <div class="two-column-layout">
          <div class="left-column">
            <div class="product-count-bar">
              <span class="product-count-badge">
                <i class="fa-solid fa-cube"></i>
                {{ parentProductCount }} {{ parentProductCount === 1 ? 'Product' : 'Products' }}
              </span>
            </div>
            <table class="product-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Description</th>
                  <th>Notes</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Per Item Discount</th>
                  <th>Total Discount</th>
                  <th>Total AUD</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in productList" :key="index">
                  <td class="product-name-cell" :title="item.product" :style="item.isChild ? 'padding-left: 2.5rem;' : ''"
                    @click.stop="startEdit(index, 'product')">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <i v-if="item.isChild" class="fas fa-level-up-alt fa-rotate-90 text-vertex-blue"></i>

                      <i v-if="item.isChild" class="fas"
                        :class="item.item_hidden ? 'fa-eye-slash text-danger' : 'fa-eye text-success'"
                        style="cursor: pointer;"
                        :title="item.item_hidden ? 'Hidden from Proposal PDF' : 'Visible on Proposal PDF'"
                        @click.stop="item.item_hidden = !item.item_hidden"></i>

                      <i v-if="!item.isChild || item.pricing > 0 || item.defaultPricing > 0" class="fa-solid fa-times remove-icon" title="Remove Product"
                        @click.stop="removeProduct(index)"></i>

                      <strong v-if="!item.isEditingProduct"
                        :style="item.item_hidden ? 'text-decoration: line-through; color: #999;' : ''">{{
                        item.product }}</strong>
                      <input v-else v-focus type="text" v-model="item.product" class="product-name-input"
                        @click.stop
                        @blur="endEdit(item, 'product')"
                        @keyup.enter="endEdit(item, 'product')" />
                    </div>
                  </td>

                  <td :title="stripHtml(item.description || '')" @click.stop="openRichTextModal(item, 'description')" style="cursor: pointer; text-align: left;">
                    <span>{{ truncateText(stripHtml(item.description), 50) }}</span>
                  </td>

                  <td :title="stripHtml(item.remarks || '')" @click.stop="openRichTextModal(item, 'remarks')" style="cursor: pointer; text-align: left;">
                    <span>{{ truncateText(stripHtml(item.remarks), 50) }}</span>
                  </td>

                  <td @click.stop="(!item.isChild || item.pricing > 0 || item.defaultPricing > 0) ? startEdit(index, 'quantity') : null">
                    <span v-if="!item.isEditingQuantity || (item.isChild && parseFloat(item.pricing) <= 0 && parseFloat(item.defaultPricing || 0) <= 0)">{{ item.quantity }}</span>
                    <input v-else type="number" v-model.number="item.quantity" @blur="endEdit(item, 'quantity')"
                      @keyup.enter="endEdit(item, 'quantity')" @input="syncChildQuantities(item)" />
                  </td>
                  <td @click.stop="(!item.isChild || item.pricing > 0 || item.defaultPricing > 0) ? startEdit(index, 'pricing') : null">
                    <template v-if="!item.isChild || item.pricing > 0 || item.defaultPricing > 0">
                      <span v-if="!item.isEditingPricing">{{ formatCurrency(item.pricing) }}</span>
                      <input v-else type="number" v-model.number="item.pricing" @blur="endEdit(item, 'pricing')"
                        @keyup.enter="endEdit(item, 'pricing')" />
                    </template>
                    <template v-else>
                      <span class="badge-included">Included</span>
                    </template>
                  </td>
                  <td @click.stop="(!item.isChild || item.pricing > 0 || item.defaultPricing > 0) ? startEdit(index, 'discount') : null">
                    <template v-if="!item.isChild || item.pricing > 0 || item.defaultPricing > 0">
                      <span v-if="!item.isEditingDiscount">{{ formatCurrency(item.discount) }}</span>
                      <input v-else type="number" v-model.number="item.discount" @blur="endEdit(item, 'discount')"
                        @keyup.enter="endEdit(item, 'discount')" />
                    </template>
                  </td>

                  <td>
                    <template v-if="!item.isChild || item.pricing > 0 || item.defaultPricing > 0">
                      {{ formatCurrency(calculateTotalDiscount(item)) }}
                    </template>
                  </td>

                  <td>
                    <template v-if="!item.isChild || item.pricing > 0 || item.defaultPricing > 0">
                      {{ formatCurrency(calculateTotal(item)) }}
                    </template>
                    <template v-else>
                      <span class="badge-included">Included</span>
                    </template>
                  </td>
                </tr>

              </tbody>
              <tfoot v-if="productList && productList.length > 0" class="pricing-summary-footer">
                <tr>
                  <td colspan="7" class="summary-label text-muted">Subtotal (Excl. GST):</td>
                  <td class="summary-value">{{ formatCurrency(calculateSubTotalPrice) }}</td>
                </tr>
                <tr>
                  <td colspan="7" class="summary-label text-muted" :title="'Goods and Services Tax'">GST (10%):</td>
                  <td class="summary-value">{{ formatCurrency(calculateTotalGST) }}</td>
                </tr>
                <tr class="total-row">
                  <td colspan="7" class="summary-label total-label">TOTAL (Incl. GST):</td>
                  <td class="summary-value total-value">{{ formatCurrency(calculateTotalPrice) }}</td>
                </tr>
                <tr>
                  <td colspan="9" style="padding: 15px 12px; background-color: #f9f9f9;">
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                      <label class="checkbox-label mb-0" @click.stop>
                        <input type="checkbox" v-model="showDiscountCalculation" class="styled-checkbox">
                        <span class="checkbox-custom"></span>
                        Show Discount Calculation on Proposal Landing Page?
                      </label>
                      <button class="button continue px-4" :disabled="isLeadMissingContactInfo" :style="isLeadMissingContactInfo ? 'opacity: 0.6; cursor: not-allowed; background-color: #ccc; border-color: #ccc;' : ''" @click="createProposal">Continue</button>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="right-column">
            <div style="padding: 10px;">
              <input type="text" v-model="searchQuery" placeholder="Search products..." class="product-search-input">
              <div class="available-count-label">
                <i class="fa-solid fa-list"></i>
                {{ filteredProducts.length }} of {{ availableProducts.length }} Available
              </div>
              <div style="max-height: 60vh; overflow-y: auto;">
                <table class="product-selection-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Price</th>
                      <th style="width: 50px; text-align: center;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(product, index) in filteredProducts" :key="index">
                      <td :title="product.name" style="color: #00bcd4"><strong>{{ product.name }}</strong></td>
                      <td>{{ formatCurrency(product.defaultPricing) }}</td>
                      <td style="width: 50px; text-align: center; padding: 5px;">
                        <button title="Add line item" class="button-select continue add-btn-small"
                          @click.stop="selectProduct(product)"><i class="fa-solid fa-plus"></i></button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

        <!-- Rich Text Editing Modal -->
        <div v-if="showRichTextModal" class="rich-text-modal-backdrop">
          <div class="rich-text-modal">
            <div class="rich-text-modal-header">
              <h5>Edit {{ richTextEditingField === 'description' ? 'Description' : 'Notes' }}</h5>
              <button type="button" class="btn-close-custom" @click="closeRichTextModal">&times;</button>
            </div>
            <div class="rich-text-modal-body">
              <div class="rich-text-toolbar">
                <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('bold')" title="Bold">
                  <i class="fa-solid fa-bold"></i>
                </button>
                <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('italic')" title="Italic">
                  <i class="fa-solid fa-italic"></i>
                </button>
                <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('underline')" title="Underline">
                  <i class="fa-solid fa-underline"></i>
                </button>
                <button type="button" class="toolbar-btn" @mousedown.prevent="execEditorCommand('insertUnorderedList')" title="Bullet List">
                  <i class="fa-solid fa-list-ul"></i>
                </button>
              </div>
              <div 
                ref="richTextEditor" 
                class="rich-text-editor" 
                contenteditable="true"
                @input="onEditorInput"
                style="text-align: left;"
              ></div>
              <div 
                class="rich-text-char-counter" 
                :style="{ color: richTextLength > 254 ? '#dc3545' : '#6c757d' }"
                v-if="richTextEditingField === 'remarks'"
              >
                {{ richTextLength }}/254
              </div>
            </div>
            <div class="rich-text-modal-footer">
              <button type="button" class="btn-cancel" @click="closeRichTextModal">Cancel</button>
              <button 
                type="button" 
                class="btn-save" 
                @click="saveRichTextContent"
                :disabled="richTextEditingField === 'remarks' && richTextLength > 254"
              >Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

<script>
import { isEmpty } from '@/plugins/helper.js'
import SimpleLoader from '@/components/Utilities/SimpleLoader.vue'
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss"
import { countries } from 'country-data'
import {
  isAustralianCountry,
  getBusinessNumberLabel,
  getBusinessNumberShortLabel,
  getBusinessNumberPlaceholder,
  validateBusinessNumber,
} from '@/plugins/businessValidator.js'

export default ({
  components: {
    SimpleLoader,
    VueMultiselect
  },

  directives: {
    focus: {
      mounted(el) {
        el.focus();
      }
    }
  },

  props: {
    proposal_name: {
      type: String,
    },
    lead_id: {
      type: String,
      required: false,
      default: ""
    },
  },

  data() {
    return {
      searchQuery: '',
      showRichTextModal: false,
      richTextEditingItem: null,
      richTextEditingField: '',
      richTextLength: 0,
      product_concat: "No Product(s)",

      // Raw data straight from APIs
      rawAuthProducts: [],
      rawInternalPricing: [],

      // All mapped products including parent-child definitions
      allMappedProducts: [],

      // Processed selection list (Right Table - ONLY ROOT ITEMS)
      availableProducts: [],

      // Added to proposal list (Left Table)
      productList: [],

      defaultBlocks: [],
      blockTypes: [],
      blockTypeFiltered: [],
      newProductIndex: -1,
      lead: null,
      isLoading: false,
      isCharityLead: false,
      BlockTypeContents: [
        { type: "CoverPage", content: "" },
        { type: "ProductService", content: "" },
        { type: "PricingTable", content: "" },
        { type: "Testimonial", content: "" },
        { type: "Content", content: "" },
        { type: "SocialImpact", content: "" },
        { type: "CommitmentToSafetyAndCompliance", content: "" },
        { type: "TermsAndConditions", content: "" },
        { type: "WHSResponsibilities", content: "" },
        { type: "NextSteps", content: "" },
        { type: "Image", content: "" }
      ],
      BlockTypeContentsProductService: [],
      BlockTypeContentsTestimonial: [],
      ContentTypeContents: [],
      testimonials: [],
      testimonial_ids: [],
      variableFieldsMapping: [
        { replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_organisation_name">` + "organisation_name" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "organisation_name" },
        { replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_abn">` + "abn" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "abn" },
        { replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_business_number">` + "business_number" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "business_number" },
        { replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_business_number_type">` + "business_number_type" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "business_number_type" },
        { replace_string: `&nbsp;<mark>{{<span style="color: red;">!*</span><span id="variable_value_business_number_formatted">` + "business_number_formatted" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "business_number_formatted" },
        { replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_contact_number">` + "contact_number" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "phone_number" },
        { replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_org_address">` + "org_address" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "org_address" },
        { replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_first_name">` + "first_name" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "first_name" },
        { replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_last_name">` + "last_name" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "last_name" },
        { replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_relations_manager">` + "relations_manager" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "relations_manager" },
        { replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_msp">` + "msp" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "msp" },
        { replace_string: `<mark>{{<span style="color: red;">!*</span><span id="variable_value_billing_email">` + "billing_email" + `</span><span style="color: red;">*!</span>}}</mark>`, replace_variable: "email" }
      ],
      cover_page_content: "",
      product_service_content: "",
      pricing_table_content: "",
      image_grid_content: "",
      testimonial_content: "",
      content_blocks_content: "",
      line_item_index: 1,
      showDiscountCalculation: false,
      organisations: [],
      vtxUsers: [],
      isIndependentProposal: false,
      leadSelectionMode: 'existing',
      newLeadStep: 1,
      selectedLead: null,
      selectedAuthOrg: null,
      orgContacts: [],
      selectedContact: null,
      pendingPreferredContact: null,
      isLoadingContacts: false,
      leadsList: [],
      leadTypes: [],
      leadPriorityTypes: [],
      countryOptions: [],
      abnDetails: null,
      abnRecordFound: false,
      abnLookup: { isLoading: false, status: 'idle', message: '' },
      abnDebounceTimer: null,
      leadForm: {
        organisationName: '',
        linkToAuthOrg: true,
        abn: '',
        isCharity: false,
        country: null,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        leadTypeID: '',
        leadPriorityID: '',
        enquiryDetails: '',
        bdm_id: ''
      },
      bdms: [],
      selectedInlineBdmId: '',
      isSavingInlineBdm: false
    };
  },

  async mounted() {
    await this.fetchData()
  },

  computed: {
    selectedLeadCannotCreateProposal() {
      if (!this.selectedLead) return false;
      const isClient = this.selectedLead.classification === 'Client' || 
                       this.selectedLead.organisation_classification === 'Client' ||
                       (this.selectedLead.classifications && this.selectedLead.classifications.some(c => c.classification === 'Client' || c === 'Client'));
      const hasProposals = (this.selectedLead.lead_proposals && this.selectedLead.lead_proposals.length > 0) || this.selectedLead.has_proposals;
      const isSynced = (this.selectedLead.organisation && this.selectedLead.organisation.has_auth_organisation === 1) || 
                       this.selectedLead.is_auth_synced === true;
      return isClient && hasProposals && !isSynced;
    },
    selectedLeadHasNoBdm() {
      if (!this.selectedLead) return false;
      return !this.selectedLead.assigned_user_id || this.selectedLead.assigned_user_id === 0;
    },
    leadHasNoBdm() {
      if (!this.lead) return false;
      return !this.lead.assigned_user_id || this.lead.assigned_user_id === 0;
    },
    isSelectedLeadAuthLinked() {
      if (!this.selectedLead) return false;
      const authSyncType = this.leadTypes.find(t => t.name.toLowerCase() === 'authsync' || t.name === 'AuthSync');
      const authSyncTypeId = authSyncType ? authSyncType.id : '';
      return (
        (authSyncTypeId && this.selectedLead.lead_type_id === authSyncTypeId) ||
        (this.selectedLead.organisation && (
          this.selectedLead.organisation.has_auth_organisation === 1 ||
          this.selectedLead.organisation.auth_organisation_id > 0
        )) ||
        (this.selectedLead.has_auth_organisation === 1) ||
        (this.selectedLead.auth_organisation_id && this.selectedLead.auth_organisation_id > 0)
      );
    },
    activeContactName() {
      if (this.selectedContact) {
        const first = this.selectedContact.first_name || this.selectedContact.firstName || '';
        const last = this.selectedContact.last_name || this.selectedContact.lastName || '';
        const combined = [first, last].filter(Boolean).join(' ');
        if (combined) return combined;
        return (this.selectedContact.fullName || this.selectedContact.name || '').trim();
      }
      if (this.selectedLead) {
        const first = this.selectedLead.first_name || this.selectedLead.firstName || '';
        const last = this.selectedLead.last_name || this.selectedLead.lastName || '';
        const combined = [first, last].filter(Boolean).join(' ');
        if (combined) return combined;
        return (this.selectedLead.contact_name || this.selectedLead.fullName || this.selectedLead.name || '').trim();
      }
      return '';
    },
    activeContactEmail() {
      if (this.selectedContact && this.selectedContact.email) {
        return this.selectedContact.email;
      }
      return this.selectedLead ? this.selectedLead.email : '';
    },
    activeContactPhone() {
      if (this.selectedContact && this.selectedContact.phone_number) {
        return this.selectedContact.phone_number;
      }
      return this.selectedLead ? this.selectedLead.phone_number : '';
    },
    selectedLeadOrgName() {
      if (!this.selectedLead) return 'Not Provided';
      return (this.selectedLead.organisation && this.selectedLead.organisation.name) || this.selectedLead.organisation_name || 'Not Provided';
    },
    selectedLeadAbn() {
      if (!this.selectedLead) return '';
      return (this.selectedLead.organisation ? this.selectedLead.organisation.abn : '') || this.selectedLead.abn || this.selectedLead.abn_info || '';
    },
    selectedLeadCountry() {
      if (!this.selectedLead) return 'Australia';
      const c = (this.selectedLead.organisation ? this.selectedLead.organisation.country : '') || this.selectedLead.country_code || 'Australia';
      if (c === 'AU' || c === 'au') return 'Australia';
      if (c === 'NZ' || c === 'nz') return 'New Zealand';
      return c;
    },
    isSelectedLeadMissingContactInfo() {
      if (!this.selectedLead) return false;
      if (!this.isSelectedLeadAuthLinked) return false;

      // If a valid contact is selected from dropdown, it is not missing contact info
      if (this.selectedContact && this.selectedContact.first_name && this.selectedContact.last_name && this.selectedContact.email) {
        return false;
      }

      // If contacts list has entries but none selected or selected is incomplete
      if (this.orgContacts && this.orgContacts.length > 0 && !this.selectedContact) {
        return true;
      }

      const email = this.activeContactEmail;
      const name = this.activeContactName;
      return !email || !name;
    },
    isLeadMissingContactInfo() {
      if (!this.lead) return false;
      const authSyncType = this.leadTypes.find(t => t.name.toLowerCase() === 'authsync' || t.name === 'AuthSync');
      const authSyncTypeId = authSyncType ? authSyncType.id : '';
      const isAuthSynced = (
        (authSyncTypeId && this.lead.lead_type_id === authSyncTypeId) ||
        (this.lead.organisation && (
          this.lead.organisation.has_auth_organisation ||
          this.lead.organisation.auth_organisation_id > 0
        )) ||
        (this.lead.auth_organisation_id && this.lead.auth_organisation_id > 0)
      );

      // If a valid selected contact was chosen, lead has contact info
      if (this.selectedContact && this.selectedContact.first_name && this.selectedContact.last_name && this.selectedContact.email) {
        return false;
      }

      return isAuthSynced && (!this.lead.email || !this.lead.first_name || !this.lead.last_name);
    },
    leadFormCountryCode() {
      return this.leadForm.country?.code || 'AU';
    },
    isAustralianLeadForm() {
      return isAustralianCountry(this.leadFormCountryCode);
    },
    leadFormBusinessNumberLabel() {
      return getBusinessNumberLabel(this.leadFormCountryCode);
    },
    leadFormBusinessNumberShortLabel() {
      return getBusinessNumberShortLabel(this.leadFormCountryCode);
    },
    leadFormBusinessNumberPlaceholder() {
      return getBusinessNumberPlaceholder(this.leadFormCountryCode);
    },
    availableOrganisationNames() {
      if (!this.abnRecordFound || !this.abnDetails) {
        return [];
      }
      const names = [];
      if (this.abnDetails.entity_name) {
        names.push(this.abnDetails.entity_name);
      }
      if (this.abnDetails.business_names && this.abnDetails.business_names.length) {
        this.abnDetails.business_names.forEach(b => {
          if (b.name && !names.includes(b.name)) {
            names.push(b.name);
          }
        });
      }
      return names;
    },
    filteredProducts() {
      if (!this.searchQuery) {
        return this.availableProducts;
      }
      return this.availableProducts.filter(product =>
        product.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    parentProductCount() {
      return this.productList.filter(p => !p.isChild).length;
    },
    calculateSubTotalPrice() {
      return this.productList.reduce((total, product) => {
        if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total; // Exclude hidden or $0 children from calculations
        const productTotal = (parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0);
        return (total + productTotal);
      }, 0).toFixed(2);
    },
    calculateTotalPrice() {
      return this.productList.reduce((total, product) => {
        if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total; // Exclude hidden or $0 children from calculations
        const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
        return (total + productTotal);
      }, 0).toFixed(2);
    },
    calculateTotalGST() {
      return this.productList.reduce((total, product) => {
        if (product.item_hidden || (product.isChild && parseFloat(product.pricing) <= 0)) return total; // Exclude hidden or $0 children from calculations
        const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
        return (total + productTotal);
      }, 0).toFixed(2);
    },

    displayedProposalTitle() {
      const orgName = (this.lead && (this.lead.organisation?.name || this.lead.organisation_name || this.lead.auth_organisation_name)) || (this.selectedLead && (this.selectedLead.organisation?.name || this.selectedLead.organisation_name || this.selectedLead.auth_organisation_name)) || 'Client';
      const firstProductName = (this.productList && this.productList.length > 0) ? (this.productList[0].product || this.productList[0].name || '').trim() : '';
      return firstProductName
        ? `${firstProductName} Proposal: Vertex x ${orgName}`
        : `Proposal: Vertex x ${orgName}`;
    },

    bdmOptions() {
      return this.bdms.map(bdm => {
        const displayName = bdm.name || (bdm.first_name + ' ' + bdm.last_name).trim() || bdm.email;
        const extraEmail = bdm.email && (bdm.name || bdm.first_name || bdm.last_name) ? ` (${bdm.email})` : '';
        return {
          ...bdm,
          displayName: `${displayName}${extraEmail}`
        };
      });
    },

    selectedProposalBDM: {
      get() {
        return this.bdmOptions.find(bdm => bdm.db_id === this.leadForm.bdm_id) || null;
      },
      set(val) {
        this.leadForm.bdm_id = val ? val.db_id : null;
      }
    },

    selectedInlineBdm: {
      get() {
        return this.bdmOptions.find(bdm => bdm.db_id === this.selectedInlineBdmId) || null;
      },
      set(val) {
        this.selectedInlineBdmId = val ? val.db_id : '';
      }
    },

    selectedProposalLeadType: {
      get() {
        return this.leadTypes.find(type => type.id === this.leadForm.leadTypeID) || null;
      },
      set(val) {
        this.leadForm.leadTypeID = val ? val.id : '';
      }
    },

    selectedProposalPriority: {
      get() {
        return this.leadPriorityTypes.find(p => p.id === this.leadForm.leadPriorityID) || null;
      },
      set(val) {
        this.leadForm.leadPriorityID = val ? val.id : '';
      }
    },
  },

  watch: {
    'leadForm.isCharity'(newVal) {
      if (newVal) {
        this.leadForm.country = this.countryOptions.find(c => c.code.toLowerCase() === 'au') || null;
      }
    },
    'currentBdm.userId'(newVal) {
        if (newVal && newVal === this.currentBdm.onBehalfOfUserId) {
           this.currentBdm.onBehalfOfUserId = "";
        }
    },
    bdms: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.leadForm.bdm_id) {
          const currentUser = this.$store.state.auth.currentUser;
          if (currentUser) {
            const myBdm = newVal.find(b => 
              (b.auth_user_id && b.auth_user_id == currentUser.id) || 
              (b.email && b.email.toLowerCase() === currentUser.email.toLowerCase())
            );
            if (myBdm) {
              this.leadForm.bdm_id = myBdm.db_id;
            }
          }
        }
      },
      immediate: true
    },
    'leadForm.country'(newVal) {
      if (!newVal || newVal.code.toLowerCase() !== 'au') {
        this.leadForm.abn = '';
        this.abnRecordFound = false;
        this.abnDetails = null;
        this.abnLookup = { isLoading: false, status: 'idle', message: '' };
      }
    },
    lead(newVal) {
      this.$emit("lead-changed", newVal);
    },
    async selectedLead(newLead) {
      if (!newLead) {
        this.orgContacts = [];
        this.selectedContact = null;
        return;
      }

      // If a merged/disabled organisation is selected, prevent selection
      if (newLead.$isDisabled || newLead.is_merged) {
        const mergedInto = newLead.merged_into_name || (newLead.organisation && newLead.organisation.merged_into_name) || 'parent organisation';
        this.$toast.warning(`"${newLead.organisation_name || newLead.displayName}" is merged into "${mergedInto}". Merged organisations are disabled. Please select the parent organisation.`);
        this.selectedLead = null;
        this.orgContacts = [];
        this.selectedContact = null;
        return;
      }

      await this.loadContactsForLead(newLead, this.pendingPreferredContact);
      this.pendingPreferredContact = null;
    }
  },

  methods: {
    getContactTypeBadgeClass(type) {
      const t = (type || '').toLowerCase();
      switch (t) {
        case 'lead':
          return 'contact-type-badge-lead';
        case 'primary':
          return 'contact-type-badge-primary';
        case 'technical':
          return 'contact-type-badge-technical';
        case 'billing':
        case 'finance':
          return 'contact-type-badge-billing';
        case 'signer':
        case 'signatory':
          return 'contact-type-badge-signer';
        case 'user':
          return 'contact-type-badge-user';
        default:
          return 'contact-type-badge-default';
      }
    },
    truncateText(text, limit) {
      if (!text) return '';
      if (text.length <= limit) return text;
      return text.substring(0, limit) + '...';
    },
    priorityClass(value) {
      let priority = this.leadPriorityTypes.filter((p) => { return p.id == value }).map((e) => e.name)
      return priority.toString().toLowerCase()
    },
    stripHtml(html) {
      if (!html) return "";
      return html.replace(/<[^>]*>/g, "");
    },
    openRichTextModal(item, field) {
      this.richTextEditingItem = item;
      this.richTextEditingField = field;
      this.showRichTextModal = true;
      let val = item[field] || "";
      val = this.convertToHtml(val);
      this.richTextLength = val.length;
      this.$nextTick(() => {
        const editor = this.$refs.richTextEditor;
        if (editor) {
          editor.innerHTML = val;
          editor.focus();
        }
      });
    },
    convertToHtml(text) {
      if (!text) return "";
      if (/<[a-z][\s\S]*>/i.test(text)) {
        return text;
      }
      return text.replace(/\r?\n/g, "<br>");
    },
    execEditorCommand(command) {
      document.execCommand(command, false, null);
      this.updateLength();
    },
    updateLength() {
      const editor = this.$refs.richTextEditor;
      if (editor) {
        this.richTextLength = editor.innerHTML.length;
      }
    },
    onEditorInput(e) {
      this.richTextLength = e.target.innerHTML.length;
    },
    closeRichTextModal() {
      this.showRichTextModal = false;
      this.richTextEditingItem = null;
      this.richTextEditingField = '';
      this.richTextLength = 0;
    },
    saveRichTextContent() {
      const editor = this.$refs.richTextEditor;
      if (editor && this.richTextEditingItem) {
        let content = editor.innerHTML;
        if (content === '<br>' || content === '<p><br></p>' || content === '<div><br></div>') {
          content = '';
        }
        this.richTextEditingItem[this.richTextEditingField] = content;
        this.hasPricingTableChanges = true;
      }
      this.closeRichTextModal();
    },

    goToStep1() {
      this.lead = null;
    },

    async loadContactsForLead(newLead, preferredContact = null) {
      this.orgContacts = [];
      this.selectedContact = null;
      if (!newLead) return;

      const orgUuid = newLead.organisation_uuid || newLead.organisation_id || (newLead.organisation ? (newLead.organisation.uuid || newLead.organisation.id) : '');
      const authOrgId = newLead.auth_organisation_id || (newLead.organisation ? newLead.organisation.auth_organisation_id : 0);

      if (orgUuid || authOrgId > 0) {
        this.isLoadingContacts = true;
        try {
          let contacts = [];
          if (orgUuid) {
            try {
              const res = await this.$axios.get(`/organisations/${orgUuid}/contacts`);
              if (res.data && res.data.contacts && res.data.contacts.length > 0) {
                contacts = res.data.contacts;
              }
            } catch (err) {
              console.warn('Failed to fetch org contacts from backend, falling back to auth users:', err);
            }
          }

          if (contacts.length === 0 && authOrgId > 0) {
            try {
              const res = await this.$axios.get(`/auth/organisations/${authOrgId}/users`);
              if (res.data && res.data.users && res.data.users.length > 0) {
                contacts = res.data.users.map(u => ({
                  id: u.id,
                  uuid: u.user_uuid || '',
                  first_name: u.first_name || '',
                  last_name: u.last_name || '',
                  email: u.email || '',
                  phone_number: '',
                  role: 'Auth User',
                  contact_type: 'user',
                  is_primary: false
                }));
              }
            } catch (err) {
              console.warn('Failed to fetch auth users fallback:', err);
            }
          }

          // If current lead has contact details, ensure it exists in the list
          const leadEmail = (newLead.email || '').trim().toLowerCase();
          const leadHasContact = newLead.first_name || newLead.last_name || newLead.email;
          if (leadHasContact) {
            const exists = contacts.some(c => (c.email || '').trim().toLowerCase() === leadEmail && leadEmail !== '');
            if (!exists) {
              contacts.unshift({
                id: 'lead-primary',
                uuid: '',
                first_name: newLead.first_name || '',
                last_name: newLead.last_name || '',
                email: newLead.email || '',
                phone_number: newLead.phone_number || '',
                role: 'Primary Contact',
                contact_type: 'lead',
                is_primary: true
              });
            }
          }

          this.orgContacts = contacts.map((c, idx) => {
            const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Unnamed Contact';
            let label = fullName;
            if (c.email) label += ` (${c.email})`;
            if (c.role) label += ` - ${c.role}`;
            if (c.contact_type) {
              label += ` [${c.contact_type.toUpperCase()}]`;
            } else if (c.is_primary) {
              label += ` [Primary]`;
            }
            return {
              ...c,
              key: c.uuid || (c.id ? String(c.id) : (c.email || `contact-${idx}`)),
              displayName: label,
              fullName
            };
          });

          if (this.orgContacts.length > 0) {
            let matchedContact = null;
            if (preferredContact) {
              const pId = preferredContact.contactId != null ? String(preferredContact.contactId).trim().toLowerCase() : '';
              const pUuid = preferredContact.contactUuid != null ? String(preferredContact.contactUuid).trim().toLowerCase() : '';
              const pEmail = preferredContact.contactEmail != null ? String(preferredContact.contactEmail).trim().toLowerCase() : '';

              matchedContact = this.orgContacts.find(c => {
                const cId = c.id != null ? String(c.id).trim().toLowerCase() : '';
                const cUuid = c.uuid != null ? String(c.uuid).trim().toLowerCase() : '';
                const cEmail = c.email != null ? String(c.email).trim().toLowerCase() : '';

                if (pUuid && (cUuid === pUuid || cId === pUuid)) return true;
                if (pId && (cId === pId || cUuid === pId)) return true;
                if (pEmail && cEmail === pEmail) return true;
                return false;
              });
            }

            if (matchedContact) {
              this.selectedContact = matchedContact;
            } else {
              const orgPrimaryByEmail = leadEmail
                ? this.orgContacts.find(c => (c.email || '').trim().toLowerCase() === leadEmail)
                : null;
              const primary = orgPrimaryByEmail || this.orgContacts.find(c => c.is_primary) || this.orgContacts[0];
              this.selectedContact = primary;
            }
          }
        } catch (err) {
          console.error('Error loading contacts for selected lead:', err);
        } finally {
          this.isLoadingContacts = false;
        }
      }
    },

    async applyPrefillOptions(options) {
      if (!options) return;
      const orgId = options.orgId ? String(options.orgId).trim().toLowerCase() : '';
      const leadId = options.leadId ? String(options.leadId).trim().toLowerCase() : '';
      const contactId = options.contactId || '';
      const contactUuid = options.contactUuid || '';
      const contactEmail = options.contactEmail || '';

      this.pendingPreferredContact = { contactId, contactUuid, contactEmail };

      // 1. Try to match from leadsList
      let matchedLead = null;
      if (this.leadsList && this.leadsList.length > 0) {
        matchedLead = this.leadsList.find(l => {
          const lId = l.id != null ? String(l.id).trim().toLowerCase() : '';
          const lUuid = l.uuid != null ? String(l.uuid).trim().toLowerCase() : '';
          const lOrgUuid = l.organisation_uuid != null ? String(l.organisation_uuid).trim().toLowerCase() : '';
          const lOrgId = l.organisation_id != null ? String(l.organisation_id).trim().toLowerCase() : '';
          const lNestedOrgUuid = (l.organisation && l.organisation.uuid) ? String(l.organisation.uuid).trim().toLowerCase() : '';
          const lNestedOrgId = (l.organisation && l.organisation.id) ? String(l.organisation.id).trim().toLowerCase() : '';

          if (leadId && (lUuid === leadId || lId === leadId)) return true;
          if (orgId && (lOrgUuid === orgId || lOrgId === orgId || lNestedOrgUuid === orgId || lNestedOrgId === orgId || lUuid === orgId || lId === orgId)) return true;
          return false;
        });
      }

      // 2. If not found in leadsList, fetch directly
      if (!matchedLead) {
        if (leadId) {
          try {
            const resp = await this.$axios.get(`/leads/${leadId}`);
            if (resp.data && resp.data.lead) {
              const l = resp.data.lead;
              const resolvedOrgUuid = l.organisation_uuid || (l.organisation && (l.organisation.id || l.organisation.uuid)) || orgId || '';
              const isSynced = !!(l.is_auth_synced || l.has_auth_organisation === 1 || (l.organisation && (l.organisation.has_auth_organisation === 1 || l.organisation.is_auth_synced)));
              matchedLead = {
                ...l,
                id: l.id || l.uuid,
                lead_id: l.id || l.uuid,
                lead_uuid: l.id || l.uuid,
                uuid: l.id || l.uuid,
                organisation_uuid: resolvedOrgUuid,
                organisation_id: resolvedOrgUuid,
                is_auth_synced: isSynced,
                uniqueKey: resolvedOrgUuid || l.id || l.uuid,
                displayName: `${l.company_name || l.name || 'Unnamed'} — ${l.email || 'No email'}`
              };
              this.leadsList.unshift(matchedLead);
            }
          } catch (e) {
            console.warn('Failed to fetch lead by leadId for prefill:', e);
          }
        }

        if (!matchedLead && orgId) {
          try {
            const orgResp = await this.$axios.get(`/organisations/${orgId}`);
            if (orgResp.data && orgResp.data.organisation) {
              const org = orgResp.data.organisation;
              if (org.lead_uuid) {
                try {
                  const leadResp = await this.$axios.get(`/leads/${org.lead_uuid}`);
                  if (leadResp.data && leadResp.data.lead) {
                    const l = leadResp.data.lead;
                    const orgUuid = org.id || org.uuid || orgId;
                    const isSynced = !!(org.is_auth_synced || org.has_auth_organisation === 1 || l.is_auth_synced || l.has_auth_organisation === 1);
                    matchedLead = {
                      ...l,
                      id: l.id || l.uuid,
                      lead_id: l.id || l.uuid,
                      lead_uuid: l.id || l.uuid,
                      uuid: l.id || l.uuid,
                      organisation_uuid: orgUuid,
                      organisation_id: orgUuid,
                      is_auth_synced: isSynced,
                      uniqueKey: orgUuid || l.id || l.uuid,
                      displayName: `${l.company_name || l.name || org.name || 'Unnamed'} — ${l.email || 'No email'}`
                    };
                    this.leadsList.unshift(matchedLead);
                  }
                } catch (leadErr) {
                  console.warn('Failed to fetch lead by org.lead_uuid for prefill:', leadErr);
                }
              }

              if (!matchedLead && org) {
                const orgUuid = org.id || org.uuid || orgId;
                const primaryContact = (org.contacts && org.contacts.length > 0) ? org.contacts[0] : {};
                const primaryAddress = (org.addresses && org.addresses.length > 0) ? org.addresses[0] : {};
                const matchedBdm = (this.bdms || []).find(b => b.auth_user_id && Number(b.auth_user_id) === Number(org.assigned_user_id));
                matchedLead = {
                  id: org.lead_uuid || orgUuid,
                  lead_id: org.lead_uuid || '',
                  lead_uuid: org.lead_uuid || '',
                  uuid: org.lead_uuid || orgUuid,
                  uniqueKey: orgUuid,
                  organisation_uuid: orgUuid,
                  organisation_id: orgUuid,
                  company_name: org.name || '',
                  trading_name: org.trading_name || '',
                  abn: org.abn || '',
                  acn: org.acn || '',
                  first_name: primaryContact.first_name || '',
                  last_name: primaryContact.last_name || '',
                  email: primaryContact.email || org.email || '',
                  phone_number: primaryContact.phone || primaryContact.mobile || org.phone_number || '',
                  website: org.website || '',
                  address_line_1: primaryAddress.line1 || primaryAddress.address_line_1 || '',
                  suburb: primaryAddress.suburb || '',
                  state: primaryAddress.state || '',
                  postcode: primaryAddress.postcode || '',
                  country: primaryAddress.country || 'Australia',
                  assigned_user_id: org.assigned_user_id || 0,
                  bdm_id: matchedBdm ? matchedBdm.db_id : (org.bdm_id || 0),
                  is_auth_synced: !!(org.is_auth_synced || org.has_auth_organisation === 1),
                  has_auth_organisation: org.has_auth_organisation || 0,
                  organisation: org,
                  contacts: org.contacts || [],
                  displayName: `${org.name || 'Unnamed'} — ${primaryContact.email || org.email || 'No email'}`
                };
                this.leadsList.unshift(matchedLead);
              }
            }
          } catch (e) {
            console.warn('Failed to fetch org for prefill:', e);
          }
        }
      }

      // 3. Apply selection and contacts
      if (matchedLead) {
        this.selectedLead = matchedLead;
        await this.loadContactsForLead(matchedLead, this.pendingPreferredContact);
        this.pendingPreferredContact = null;

        if (!this.selectedLeadHasNoBdm && !this.selectedLeadCannotCreateProposal && !this.isSelectedLeadMissingContactInfo) {
          await this.proceedWithExistingLead();
        }
      }
    },

    async init(id, options = null) {
      this.isLoading = true;
      this.getVertexUsers()
      await Promise.all([
        this.getAuthProducts(),
        this.getInternalPricing()
      ]);
      this.buildAvailableProductsList();
      this.getAuthOrganisations()
      
      if (isEmpty(id)) {
        this.isIndependentProposal = true;
        this.lead = null;
        this.selectedLead = null;
        this.selectedAuthOrg = null;
        this.orgContacts = [];
        this.selectedContact = null;
        this.pendingPreferredContact = null;
        this.leadSelectionMode = 'existing';
        this.newLeadStep = 1;
        this.leadForm = {
          organisationName: '',
          linkToAuthOrg: true,
          abn: '',
          isCharity: false,
          country: null,
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          leadTypeID: '',
          leadPriorityID: '',
          enquiryDetails: '',
          bdm_id: null
        };
        this.countryOptions = countries.all.map(c => ({
          name: c.name,
          code: c.alpha2 || c.alpha3 || c.code
        })).filter(c => c.name && c.code);
        this.leadForm.country = this.countryOptions.find(c => c.code.toLowerCase() === 'au') || null;
        await this.getBDMs();
        await this.fetchLeadSelectionMetadata();

        if (options && (options.orgId || options.leadId || options.contactId || options.contactUuid || options.contactEmail)) {
          await this.applyPrefillOptions(options);
        }
      } else {
        this.isIndependentProposal = false;
        await this.getLead(id)
      }
      this.isLoading = false;
    },

    async fetchLeadSelectionMetadata() {
      try {
        if (!this.bdms || this.bdms.length === 0) {
          await this.getBDMs();
        }
        const [orgsResp, typesResp, prioritiesResp] = await Promise.all([
          this.$axios.get("/organisations?pageSize=1000&includeMerged=true"),
          this.$axios.get("/categories?name=LeadType"),
          this.$axios.get("/categories?name=LeadPriority")
        ]);
        
        const rawOrgs = orgsResp.data.organisations || [];
        
        this.leadsList = rawOrgs.map(org => {
          const orgName = org.name || "Unnamed Organisation";
          const isMerged = !!org.is_merged;
          const mergedIntoName = org.merged_into_name || "";
          const mergedIntoUuid = org.merged_into_uuid || "";
          const contactName = [org.primary_contact_first_name, org.primary_contact_last_name].filter(Boolean).join(" ");
          const contactEmail = org.primary_contact_email || org.email || "";

          let displayName = orgName;
          if (isMerged) {
            displayName += mergedIntoName ? ` [Merged to: ${mergedIntoName}] (Disabled)` : ` [Merged - Disabled]`;
          } else {
            if (contactName) {
              displayName += ` - ${contactName}`;
            }
            if (contactEmail) {
              displayName += ` (${contactEmail})`;
            }
          }

          const orgUuid = org.id || org.uuid || '';
          const matchedBdm = (this.bdms || []).find(b => b.auth_user_id && Number(b.auth_user_id) === Number(org.assigned_user_id));

          return {
            id: org.lead_uuid || orgUuid,
            lead_id: org.lead_uuid || '',
            lead_uuid: org.lead_uuid || '',
            uuid: org.lead_uuid || orgUuid,
            uniqueKey: orgUuid || String(org.id),
            organisation_uuid: orgUuid,
            organisation_id: orgUuid,
            organisation_name: org.name,
            name: org.name,
            abn: org.abn || '',
            abn_info: org.abn || '',
            country_code: org.country || 'Australia',
            country: org.country || 'Australia',
            is_charity: !!org.is_charity,
            first_name: org.primary_contact_first_name || '',
            last_name: org.primary_contact_last_name || '',
            email: org.primary_contact_email || org.email || '',
            phone_number: org.primary_contact_phone || org.phone_number || '',
            has_auth_organisation: org.has_auth_organisation || 0,
            auth_organisation_id: org.auth_organisation_id || 0,
            auth_organisation_name: org.auth_organisation_name || '',
            is_auth_synced: !!(org.is_auth_synced || org.has_auth_organisation === 1),
            assigned_user_id: org.assigned_user_id || 0,
            bdm_id: matchedBdm ? matchedBdm.db_id : (org.bdm_id || 0),
            classification: (org.classifications && org.classifications.length > 0) ? org.classifications[0].classification : (org.classification || 'Client'),
            classifications: (org.classifications && org.classifications.length > 0) ? org.classifications : [{ classification: org.classification || 'Client' }],
            is_merged: isMerged,
            merged_into_name: mergedIntoName,
            merged_into_uuid: mergedIntoUuid,
            $isDisabled: isMerged,
            displayName,
            organisation: org
          };
        });
        
        this.leadTypes = typesResp.data.category.subcategories || [];
        this.leadPriorityTypes = prioritiesResp.data.category.subcategories || [];
        
        if (this.leadTypes.length > 0) {
          this.leadForm.leadTypeID = this.leadTypes[0].id;
        }
        if (this.leadPriorityTypes.length > 0) {
          const medPriority = this.leadPriorityTypes.find(p => p.name.toLowerCase() === 'medium') || this.leadPriorityTypes[0];
          this.leadForm.leadPriorityID = medPriority.id;
        }
      } catch (error) {
        console.error("Failed to fetch lead selection metadata", error);
        this.$toast.error("Failed to load lead forms configuration.");
      }
    },

    handleAbnInput() {
      clearTimeout(this.abnDebounceTimer);
      if (!this.leadForm.abn || this.leadForm.abn.trim() === '') {
        this.abnRecordFound = false;
        this.abnDetails = null;
        this.abnLookup = { isLoading: false, status: 'idle', message: '' };
        return;
      }
      this.abnDebounceTimer = setTimeout(() => { this.lookupABN(); }, 1000);
    },

    async lookupABN() {
      if (!this.leadForm.abn || this.leadForm.abn.trim() === '') {
        this.abnRecordFound = false;
        this.abnDetails = null;
        this.abnLookup = { isLoading: false, status: 'idle', message: '' };
        return;
      }

      if (!this.isAustralianLeadForm) {
        const isValid = validateBusinessNumber(this.leadForm.abn, this.leadFormCountryCode);
        if (isValid) {
          this.abnLookup = { isLoading: false, status: 'success', message: `${this.leadFormBusinessNumberShortLabel} format valid.` };
        } else {
          this.abnLookup = { isLoading: false, status: 'error', message: `Invalid ${this.leadFormBusinessNumberShortLabel} format.` };
        }
        this.abnRecordFound = false;
        this.abnDetails = null;
        return;
      }

      const abn = this.leadForm.abn.replace(/\s/g, '');
      if (!abn || (abn.length !== 11 && abn.length !== 9)) {
        this.abnLookup = { isLoading: false, status: 'error', message: abn.length > 0 ? 'ABN must be 11 digits (or 9 digits for ACN).' : '' };
        this.abnRecordFound = false;
        this.abnDetails = null;
        return;
      }
      this.abnLookup.isLoading = true;
      this.abnLookup.message = '';
      this.abnLookup.status = 'idle';
      try {
        const resp = await this.$axios.post('/abn/look-up', {
          abn: abn,
        });
        const details = resp.data.abn_details;
        this.abnDetails = details;
        this.abnRecordFound = true;

        if (this.leadForm.isCharity && !details.is_charity) {
          this.abnLookup.status = 'error';
          this.abnLookup.message = 'ABN is found but not registered as a charity.';
        } else {
          this.abnLookup.status = 'success';
          this.abnLookup.message = 'ABN Validated successfully.';
          this.leadForm.organisationName = details.entity_name;
        }
      } catch (error) {
        this.abnRecordFound = false;
        this.abnDetails = null;
        this.abnLookup.status = 'error';
        this.abnLookup.message = error.response?.data?.message || 'Invalid ABN';
      } finally {
        this.abnLookup.isLoading = false;
      }
    },

    openLeadInAuth(lead) {
      const targetLead = lead || this.selectedLead || this.lead;
      const orgId = targetLead?.auth_organisation_id ||
                    targetLead?.organisation?.auth_organisation_id ||
                    targetLead?.organisation_auth_organisation_id ||
                    targetLead?.authOrganisationId;
      if (targetLead && orgId) {
        const authBaseUrl = this.$config.getAuthBaseUrl();
        const accessToken = this.$store.state.auth.accessToken;
        const url = `${authBaseUrl}/organisations/${orgId}/settings/?access_token=${accessToken}&org_selected=${orgId}`;
        window.open(url, '_blank');
      } else {
        this.$toast.warning("This lead is not linked to an Auth organisation.");
      }
    },

    async proceedWithExistingLead() {
      if (!this.selectedLead) {
        this.$toast.error("Please select a lead.");
        return;
      }
      if (this.selectedLead.$isDisabled || this.selectedLead.is_merged) {
        const mergedInto = this.selectedLead.merged_into_name || (this.selectedLead.organisation && this.selectedLead.organisation.merged_into_name) || 'parent organisation';
        this.$toast.warning(`"${this.selectedLead.organisation_name || this.selectedLead.displayName}" is merged into "${mergedInto}". Merged organisations are disabled. Please select the parent organisation.`);
        return;
      }
      if (this.selectedLeadHasNoBdm) {
        this.$toast.error("Cannot proceed: Selected client has no BDM assigned. Please assign a BDM first.");
        return;
      }
      if (this.selectedLeadCannotCreateProposal) {
        this.$toast.error("Cannot proceed: Additional proposals can only be created for Client organisations that are fully synced with Auth.");
        return;
      }
      if (this.isSelectedLeadMissingContactInfo) {
        this.$toast.error("Cannot proceed: Selected synced client has incomplete contact details. Please select a valid contact or update their details in Auth.");
        return;
      }
      this.isLoading = true;
      const orgUuid = this.selectedLead.organisation_uuid || this.selectedLead.organisation_id || (this.selectedLead.organisation && (this.selectedLead.organisation.id || this.selectedLead.organisation.uuid)) || '';
      const targetLeadId = this.selectedLead.lead_uuid || this.selectedLead.lead_id || (this.selectedLead.id !== orgUuid ? this.selectedLead.id : '');
      if (targetLeadId) {
        try {
          await this.getLead(targetLeadId);
        } catch (e) {
          console.warn("getLead failed, constructing fallback lead:", e);
          this.lead = null;
        }
      } else {
        this.lead = null;
      }

      if (!this.lead) {
        const matchedBdm = (this.bdms || []).find(b =>
          (b.auth_user_id && Number(b.auth_user_id) === Number(this.selectedLead.assigned_user_id)) ||
          (b.db_id && Number(b.db_id) === Number(this.selectedLead.bdm_id || this.selectedLead.assigned_user_id))
        );
        this.lead = {
          id: this.selectedLead.id,
          uuid: this.selectedLead.uuid,
          organisation_uuid: orgUuid,
          organisation_name: this.selectedLead.organisation_name || (this.selectedLead.organisation && this.selectedLead.organisation.name),
          abn: this.selectedLead.abn || (this.selectedLead.organisation && this.selectedLead.organisation.abn),
          first_name: this.selectedLead.first_name,
          last_name: this.selectedLead.last_name,
          email: this.selectedLead.email,
          phone_number: this.selectedLead.phone_number,
          country_code: this.selectedLead.country_code || 'AU',
          has_auth_organisation: this.selectedLead.has_auth_organisation,
          auth_organisation_id: this.selectedLead.auth_organisation_id,
          auth_organisation_name: this.selectedLead.auth_organisation_name,
          is_auth_synced: this.selectedLead.is_auth_synced,
          assigned_user_id: matchedBdm && matchedBdm.auth_user_id ? matchedBdm.auth_user_id : (this.selectedLead.assigned_user_id || 0),
          bdm_id: matchedBdm ? matchedBdm.db_id : (this.selectedLead.bdm_id || 0),
          is_charity: this.selectedLead.is_charity,
          classification: 'Client',
          organisation: this.selectedLead.organisation || this.selectedLead
        };
        this.productList = [];
      }

      // Ensure parent organisation details take precedence if lead is linked/merged
      if (this.lead && this.lead.organisation) {
        if (this.lead.organisation.name) {
          this.lead.organisation_name = this.lead.organisation.name;
        }
        if (this.lead.organisation.abn) {
          this.lead.abn = this.lead.organisation.abn;
        }
      }

      // Apply selected contact details to this.lead for proposal generation
      if (this.selectedContact) {
        if (this.selectedContact.first_name || this.selectedContact.last_name) {
          this.lead.first_name = this.selectedContact.first_name;
          this.lead.last_name = this.selectedContact.last_name;
        }
        if (this.selectedContact.email) {
          this.lead.email = this.selectedContact.email;
        }
        if (this.selectedContact.phone_number) {
          this.lead.phone_number = this.selectedContact.phone_number;
        }
        this.lead.selected_contact = this.selectedContact;
      }

      this.isLoading = false;
    },

    async assignBdmInline() {
      if (!this.selectedInlineBdmId) {
        this.$toast.error("Please select a BDM first.");
        return;
      }
      this.isSavingInlineBdm = true;
      try {
        const leadObj = this.selectedLead || this.lead;
        if (!leadObj) return;

        const selectedBdm = (this.bdmOptions || []).find(b => Number(b.db_id) === Number(this.selectedInlineBdmId)) ||
                            (this.bdms || []).find(b => Number(b.db_id) === Number(this.selectedInlineBdmId));
        const bdmDbId = selectedBdm ? Number(selectedBdm.db_id) : parseInt(this.selectedInlineBdmId, 10);
        const bdmAuthUserId = (selectedBdm && selectedBdm.auth_user_id) ? Number(selectedBdm.auth_user_id) : bdmDbId;

        const orgUuid = leadObj.organisation_uuid ||
                        leadObj.organisation_id ||
                        (leadObj.organisation && (leadObj.organisation.id || leadObj.organisation.uuid)) ||
                        (!leadObj.lead_uuid && !leadObj.lead_id ? leadObj.id : '');
        const targetLeadId = leadObj.lead_uuid || leadObj.lead_id || (leadObj.id !== orgUuid ? leadObj.id : '');

        let updatedLeadUuid = targetLeadId;

        const httpClient = this.$axios || this.$http;

        if (orgUuid) {
          const orgResp = await httpClient.get(`/organisations/${orgUuid}`);
          const orgData = orgResp.data.organisation || orgResp.data;
          if (!orgData) throw new Error('Could not fetch existing organisation record.');

          const classificationsPayload = (orgData.classifications || [])
            .map(c => ({ classification: typeof c === 'object' ? c.classification : c }))
            .filter(c => c.classification);

          const cleanOrgPayload = { ...orgData };
          delete cleanOrgPayload.assigned_user;
          delete cleanOrgPayload.vertex_org_manager;
          delete cleanOrgPayload.msp;
          delete cleanOrgPayload.partner;
          delete cleanOrgPayload.ecosystem;
          delete cleanOrgPayload.merged_organisations;
          delete cleanOrgPayload.original_lead_details;

          const updateOrgResp = await httpClient.put(`/organisations/${orgUuid}`, {
            ...cleanOrgPayload,
            assigned_user_id: bdmAuthUserId,
            classifications: classificationsPayload
          });
          const updatedOrg = (updateOrgResp && updateOrgResp.data && updateOrgResp.data.organisation) || null;
          if (updatedOrg && updatedOrg.lead_uuid) {
            updatedLeadUuid = updatedOrg.lead_uuid;
          } else if (orgData.lead_uuid) {
            updatedLeadUuid = orgData.lead_uuid;
          }
          if (updatedLeadUuid) {
            try {
              await httpClient.put(`/leads/${updatedLeadUuid}/assigned-user`, {
                bdm_id: bdmDbId,
                assigned_user_id: bdmAuthUserId
              });
            } catch (e) {
              // Ignore if associated lead is already synced via Organisation.Update
            }
          }
        } else if (targetLeadId) {
          await httpClient.put(`/leads/${targetLeadId}/assigned-user`, {
            bdm_id: bdmDbId,
            assigned_user_id: bdmAuthUserId
          });
        } else {
          throw new Error('Could not determine target organisation or lead identifier for BDM assignment.');
        }

        this.$toast.success("BDM assigned successfully!");
        
        // Refresh leads list & local state
        await this.fetchLeadSelectionMetadata();
        
        if (this.selectedLead) {
          const matched = (this.leadsList || []).find(l =>
            (orgUuid && (l.organisation_uuid === orgUuid || l.organisation_id === orgUuid || l.id === orgUuid)) ||
            (updatedLeadUuid && (l.lead_uuid === updatedLeadUuid || l.id === updatedLeadUuid)) ||
            (leadObj.id && l.id === leadObj.id)
          );
          if (matched) {
            this.selectedLead = matched;
          }
          this.selectedLead.assigned_user_id = bdmAuthUserId;
          this.selectedLead.bdm_id = bdmDbId;
          if (updatedLeadUuid) {
            this.selectedLead.lead_uuid = updatedLeadUuid;
            this.selectedLead.lead_id = updatedLeadUuid;
            this.selectedLead.id = updatedLeadUuid;
          }
        }
        if (this.lead) {
          this.lead.assigned_user_id = bdmAuthUserId;
          this.lead.bdm_id = bdmDbId;
          if (updatedLeadUuid) {
            this.lead.lead_uuid = updatedLeadUuid;
            this.lead.lead_id = updatedLeadUuid;
            this.lead.id = updatedLeadUuid;
          }
        }
        this.selectedInlineBdmId = "";
      } catch (error) {
        console.error("Error assigning BDM inline:", error);
        this.$toast.error("Failed to assign BDM. Please try again.");
      } finally {
        this.isSavingInlineBdm = false;
      }
    },

    handleOrgInput() {
      if (this.leadForm.organisationName) {
        this.leadForm.organisationName = this.leadForm.organisationName.replace(/[<>"\\`]/g, '');
      }
    },

    async createLeadAndProceed() {
      if (this.leadForm.organisationName) {
        this.leadForm.organisationName = this.leadForm.organisationName.replace(/[<>"\\`]/g, '').trim();
      }
      if (!this.leadForm.bdm_id) {
        this.$toast.error("Please select a BDM to attribute this lead to.");
        return;
      }
      if (!this.leadForm.organisationName) {
        this.$toast.error("Organisation Name is required.");
        return;
      }
      if (!this.leadForm.country) {
        this.$toast.error("Country is required.");
        return;
      }
      if (!this.leadForm.firstName || !this.leadForm.firstName.trim()) {
        this.$toast.error("Contact First Name is required.");
        return;
      }
      if (!this.leadForm.email) {
        this.$toast.error("Contact Email is required.");
        return;
      }
      if (!this.leadForm.phoneNumber) {
        this.$toast.error("Contact Phone Number is required.");
        return;
      }
      if (this.leadForm.abn && this.abnLookup.status !== 'success') {
        this.$toast.error(this.isAustralianLeadForm ? "Please provide a valid ABN registered in Australia." : `Please provide a valid ${this.leadFormBusinessNumberShortLabel}.`);
        return;
      }
      if (!this.leadForm.leadTypeID) {
        this.$toast.error("Lead Type is required.");
        return;
      }
      if (!this.leadForm.leadPriorityID) {
        this.$toast.error("Lead Priority is required.");
        return;
      }
      if (!this.leadForm.enquiryDetails || !this.leadForm.enquiryDetails.trim()) {
        this.$toast.error("Enquiry Details / Note is required.");
        return;
      }
      
      this.isLoading = true;
      const payload = {
        organisation_name: this.leadForm.organisationName,
        country_code: this.leadForm.country ? this.leadForm.country.code : "",
        first_name: this.leadForm.firstName.trim(),
        last_name: this.leadForm.lastName || "TBD",
        email: this.leadForm.email || "",
        phone_number: this.leadForm.phoneNumber,
        abn: this.leadForm.abn,
        is_charity: this.leadForm.isCharity,
        lead_type_id: this.leadForm.leadTypeID,
        lead_priority_id: this.leadForm.leadPriorityID,
        enquiry_details: this.leadForm.enquiryDetails,
        bdm_id: this.leadForm.bdm_id ? parseInt(this.leadForm.bdm_id) : 0,
        products: []
      };
      
      if (this.selectedAuthOrg && this.leadForm.linkToAuthOrg) {
        payload.has_auth_organisation = 1;
        payload.auth_organisation_id = this.selectedAuthOrg.id;
        payload.auth_organisation_name = this.selectedAuthOrg.name;
        payload.auth_organisation_uuid = this.selectedAuthOrg.uuid || "";
      }
      
      if (this.leadForm.enquiryDetails) {
        payload.lead_notes = [{
          note_details: this.leadForm.enquiryDetails,
          note_type: "initial_note_internal"
        }];
      }
      
      payload.bdm_id = this.leadForm.bdm_id ? parseInt(this.leadForm.bdm_id) : 0;
      
      await this.$axios.post("/leads", payload)
        .then(async (response) => {
          if (response.data.success) {
            this.$toast.success("Lead created successfully.");
            const newLeadId = response.data.lead.id;
            await this.getLead(newLeadId);
          } else {
            this.$toast.error(response.data.message || "Failed to create lead.");
          }
        })
        .catch((error) => {
          this.$toast.error(error.response?.data?.message || "Failed to create lead.");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    resetLeadSelection() {
      this.lead = null;
      this.selectedLead = null;
      this.selectedAuthOrg = null;
      this.orgContacts = [];
      this.selectedContact = null;
      this.isLoadingContacts = false;
      this.leadSelectionMode = 'existing';
      this.newLeadStep = 1;
      this.leadForm = {
        organisationName: '',
        linkToAuthOrg: true,
        abn: '',
        isCharity: false,
        country: this.countryOptions.find(c => c.code.toLowerCase() === 'au') || null,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        leadTypeID: this.leadTypes.length > 0 ? this.leadTypes[0].id : '',
        leadPriorityID: this.leadPriorityTypes.length > 0 ? (this.leadPriorityTypes.find(p => p.name.toLowerCase() === 'medium')?.id || this.leadPriorityTypes[0].id) : '',
        enquiryDetails: '',
        bdm_id: ''
      };
      this.abnLookup = { isLoading: false, status: 'idle', message: '' };
      this.abnRecordFound = false;
      this.abnDetails = null;
    },

    isUUID(uuid) {
      if (typeof uuid !== 'string') return false;
      if (uuid.length !== 36) return false;
      if (uuid[8] !== '-' || uuid[13] !== '-' || uuid[18] !== '-' || uuid[23] !== '-') return false;
      return true;
    },

    async getAuthProducts() {
      await this.$axios.get("/auth-products")
        .then((response) => {
          this.rawAuthProducts = response.data.authProducts || [];
        })
        .catch((error) => {
          console.error("Failed to fetch Auth Products", error);
        });
    },

    async getInternalPricing() {
      await this.$axios.get("/get-product-pricing")
        .then((response) => {
          this.rawInternalPricing = response.data.productPrices || [];
        })
        .catch((error) => {
          console.error("Failed to fetch internal pricing", error);
        });
    },
    
    async getBDMs() {
      try {
        const response = await this.$axios.get("/vertex-bdm");
        if (response.data.success) {
          this.bdms = response.data.bdms || [];
          // Auto-select logic
          if (this.bdms.length > 0 && !this.leadForm.bdm_id) {
            const currentUser = this.$store.state.auth.currentUser;
            
            // Find my own BDM record first
            const myBdm = this.bdms.find(b => 
              (b.auth_user_id && b.auth_user_id == currentUser.id) || 
              (b.email && b.email.toLowerCase() === currentUser.email.toLowerCase())
            );

            if (myBdm) {
              this.leadForm.bdm_id = myBdm.db_id;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching BDMs:", error);
      }
    },

    buildAvailableProductsList() {
      this.availableProducts = [];
      this.allMappedProducts = [];

      this.rawAuthProducts.forEach(authItem => {
        if (!authItem.Product) return;

        let price = authItem.Product.price ?? authItem.Product.Price ?? authItem.price ?? authItem.Price ?? 0;
        let description = authItem.Product.description ?? authItem.Product.Description ?? authItem.description ?? "No Product Description";
        let name = authItem.Product.display_name ?? authItem.Product.name ?? "No Name";
        let isCharity = authItem.is_charity_product === true || authItem.is_charity_product == 1;

        // Grab parent_product_ids from the auth payload (supports both old string and new array format)
        let parentIds = authItem.parent_product_ids || authItem.ParentProductIDs || authItem.parent_product_id || authItem.ParentProductID || null;
        if (typeof parentIds === 'string') {
           try { parentIds = JSON.parse(parentIds); } catch(e) { parentIds = parentIds ? [parentIds] : []; }
        }
        if (!Array.isArray(parentIds)) parentIds = parentIds ? [parentIds] : [];
        parentIds = parentIds.filter(id => id && id !== '');

        this.allMappedProducts.push({
          id: authItem.product_id || authItem.Product.id,
          name: name,
          defaultPricing: price,
          description: description,
          defaultQuantity: 1,
          isCharityProduct: isCharity,
          parentIds: parentIds,
          contractType: authItem.contract_type || (authItem.Product && authItem.Product.contract_type) || authItem.contract_basis || (authItem.Product && authItem.Product.contract_basis) || "",
          pricingPlanType: authItem.pricing_plan_type || (authItem.Product && authItem.Product.pricing_plan_type) || ""
        });
      });

      // Show ONLY parent products (roots) in the right selection table
      // Failsafe: If a child's parent doesn't exist in the list, treat the child as a root so it isn't hidden forever
      this.availableProducts = this.allMappedProducts.filter(p => {
        if (!p.parentIds || p.parentIds.length === 0) return true;

        const parentExists = p.parentIds.some(pid => this.allMappedProducts.some(parent => parent.id === pid));
        return !parentExists;
      });

      this.availableProducts.sort((a, b) => a.name.localeCompare(b.name));
    },

    convertLeadProductsToSelectedProducts(products_array, is_charity) {
      if (isEmpty(products_array) || products_array.length === 0) return;

      products_array.forEach((leadProduct) => {
        let targetAuthId = leadProduct.product_id;

        if (this.isUUID(targetAuthId)) {
          const internalProductLink = this.rawInternalPricing.find(p => p.ProductID === targetAuthId);
          if (internalProductLink && internalProductLink.HasAuthProductLinked == 1 && internalProductLink.AuthItemCode) {
            targetAuthId = internalProductLink.AuthItemCode;
          } else {
            return;
          }
        }

        // Check if item is already added to prevent duplicating child rows
        if (this.productList.some(p => p.id === targetAuthId)) return;

        let productFromList = this.allMappedProducts.find(item => item.id === targetAuthId);

        if (productFromList) {
          productFromList['is_charity'] = is_charity;
          this.selectProduct(productFromList);
        }
      });
    },

    formatDateToMDDYY(date) {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      const month = monthNames[date.getMonth()];
      const year = String(date.getFullYear());
      return `${month} ${year}`;
    },

    async getDefaultBlocks() {
      await this.$axios.get("/blocks/default")
        .then((response) => {
          this.defaultBlocks = response.data.blocks
        })
        .catch((error) => {
          this.$toast.error(error.response.data.message)
        })
    },

    async getBlockTypes() {
      await this.$axios.get("/categories?name=BlockType")
        .then((response) => {
          this.blockTypes = response.data.category.subcategories
          if (this.blockTypes != undefined && this.blockTypes != null && this.blockTypes.length > 0) {
            this.blockTypes.forEach((bt) => {
              this.blockTypeFiltered.push({
                "id": bt.id,
                "display_name": bt.display_name,
                "type": bt.name
              })
            })
          }
        })
        .catch((error) => {
          this.$toast.error(error.response.data.message)
        })
    },

    getDefaultContent(blockType) {
      const mContent = (this.BlockTypeContents || []).find(btc => btc.type == blockType)
      return mContent ? mContent.content : ""
    },

    getDefaultContentTypeContent(contentType) {
      const mContent = this.ContentTypeContents.find(ctc => ctc.content_type_name == contentType)
      if (mContent) return mContent.content
      return null
    },

    getDefaultContentProductService(productList) {
      if (isEmpty(productList) || productList.length === 0) return "";

      // Build a canonical set of all active product IDs, expanding each to
      // include both its auth code and internal UUID forms so that block
      // matching works regardless of how the block's products were saved.
      const activeProductIds = new Set();
      productList.forEach(p => {
        if (!p.id) return;
        activeProductIds.add(p.id.toString());

        // Expand: if it's an auth code, also add the linked internal UUIDs
        this.rawInternalPricing
          .filter(ip => ip.HasAuthProductLinked == 1 && ip.AuthItemCode == p.id)
          .forEach(ip => activeProductIds.add(ip.ProductID.toString()));

        // Expand: if it's an internal UUID, also add the linked auth code
        this.rawInternalPricing
          .filter(ip => ip.HasAuthProductLinked == 1 && ip.ProductID == p.id)
          .forEach(ip => activeProductIds.add(ip.AuthItemCode.toString()));
      });

      // Block-centric matching: for each ProductService block, check if ANY
      // of its linked products is in our canonical set. Deduplicate by block_id.
      let finalBlockContent = "";
      const usedBlockIds = new Set();

      this.BlockTypeContentsProductService.forEach(block => {
        if (usedBlockIds.has(block.block_id)) return;

        const hasMatch = block.product_ids.some(pid => activeProductIds.has(pid.toString()));
        if (hasMatch && !isEmpty(block.content)) {
          const blockId = `product_service_${block.block_id}`;
          usedBlockIds.add(block.block_id);
          finalBlockContent += `<div id='${blockId}'>${block.content}</div>`;
        }
      });

      return finalBlockContent;
    },

    GetOrganisationName(org_id) {
      const organisation = this.organisations.find(org => org.id === org_id);
      return organisation ? organisation.name : null;
    },

    async getAuthOrganisations() {
      await this.$axios_auth.get("/vertex/organisations")
        .then((response) => {
          this.organisations = response.data.organisations
        })
        .catch((error) => {
          this.$toast.error(error.response.data.message)
        })
    },

    async fetchData() {
      try {
        const [defaultBlocksResponse, blockTypesResponse, testimonialsResponse] = await Promise.all([
          this.$axios.get("/blocks/default"),
          this.$axios.get("/categories?name=BlockType"),
          this.$axios.get("/testimonials/no-filter-testimonials")
        ]);

        this.defaultBlocks = defaultBlocksResponse.data.blocks;
        this.blockTypes = blockTypesResponse.data.category.subcategories;
        this.blockTypeFiltered = [];
        this.testimonials = testimonialsResponse.data.testimonials

        if (this.blockTypes && this.blockTypes.length > 0) {
          this.blockTypes.forEach((bt) => {
            this.blockTypeFiltered.push({
              "id": bt.id,
              "display_name": bt.display_name,
              "type": bt.name
            });
          });

          this.defaultBlocks.forEach((db) => {
            if (db.BlockType && this.BlockTypeContents) {
              const matchingBlockType = this.blockTypeFiltered.find(bt => bt.id == db.BlockType.id);
              if (matchingBlockType) {
                const mContent = this.BlockTypeContents.find(btc => btc.type == matchingBlockType.type)
                if (mContent)
                  mContent.content = db.content;
              }

              if (db.BlockType.name == "ProductService") {
                if (db.products != undefined && db.products != null && db.products.length > 0)
                  this.BlockTypeContentsProductService.push(
                    {
                      block_id: db.id,
                      product_ids: db.products.map(p => p.product_id?.toString() || p.Product?.id?.toString()).filter(Boolean),
                      content: db.content
                    }
                  )
              }

              if (db.BlockType.name == "Content") {
                if (db.content_type != undefined && db.content_type != null)
                  this.ContentTypeContents.push({
                    content_type_name: db.content_type.name,
                    content: db.content,
                    blockType: db.BlockType.name
                  })
              }
            }
          });

        }
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'An error occurred while fetching data.');
      }
    },

    formatCurrency(value) {
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) {
        return '$0.00';
      }
      return `$${numericValue.toFixed(2)}`;
    },
    calculateTotal(item) {
      return ((item.quantity * item.pricing) - (item.discount * item.quantity));
    },
    calculateTotalDiscount(item) {
      return (item.discount * item.quantity);
    },
    saveDraft() {
      console.log('Save draft');
    },

    async createProposal() {
      if (!this.lead || !this.lead.assigned_user_id) {
        this.$toast.error("You cannot create this proposal because the associated Lead has no BDM assigned to it. Please assign a BDM first.");
        return;
      }
      const authSyncType = this.leadTypes.find(t => t.name.toLowerCase() === 'authsync' || t.name === 'AuthSync');
      const authSyncTypeId = authSyncType ? authSyncType.id : '';
      const isAuthSynced = this.lead && (
        (authSyncTypeId && this.lead.lead_type_id === authSyncTypeId) ||
        (this.lead.organisation && (
          this.lead.organisation.has_auth_organisation ||
          this.lead.organisation.auth_organisation_id > 0
        ))
      );
      if (isAuthSynced && (!this.lead.email || !this.lead.first_name || !this.lead.last_name)) {
        this.$toast.error("Cannot create proposal: The selected synced Lead is missing a valid First Name, Last Name, or Email address. Please edit the Organisation Lead in the Auth Portal and synchronise again in Spark to continue.");
        return;
      }
      if (isEmpty(this.productList)) {
        alert("No selected products to create proposal.");
        return
      }
      this.isLoading = true
      try {
        // Content is now automated on the backend via BuildProposalSectionContent
        this.buildContent()
        let owner_name = this.getVertexAuthUser(this.$store.state.auth.currentUser?.id)
        if (!owner_name || owner_name === "Deleted User" || owner_name === "System Generated") {
          const cu = this.$store.state.auth.currentUser;
          if (cu && (cu.first_name || cu.last_name)) {
            owner_name = `${cu.first_name || ''} ${cu.last_name || ''}`.trim();
          } else if (cu && cu.email) {
            owner_name = cu.email;
          }
        }

        let leadProposal = {
          lead_id: this.lead.id.toString(),
          proposal_title: this.displayedProposalTitle,
          auth_organisation_id: this.lead?.auth_organisation_id || this.lead?.organisation?.auth_organisation_id || 0,
          proposal_total_amount: this.calculateTotalPrice,
          proposal_owner: owner_name,
          proposal_owner_email: this.$store.state.auth.currentUser?.email || '',
          show_discount_calculation: this.showDiscountCalculation,
          bdm_id: (this.lead && this.lead.bdm_id) ? this.lead.bdm_id : this.leadForm.bdm_id,
          contact_uuid: this.selectedContact?.uuid || '',
          recipient_first_name: this.selectedContact?.first_name || this.lead.first_name || '',
          recipient_last_name: this.selectedContact?.last_name || this.lead.last_name || '',
          recipient_email: this.selectedContact?.email || this.lead.email || '',
          recipient_phone_number: this.selectedContact?.phone_number || this.lead.phone_number || '',
          recipient_contact_type: this.selectedContact?.contact_type || ''
        }

        leadProposal.proposal_items = []
        this.productList.forEach((p) => {
          leadProposal.proposal_items.push({
            product_id: p.id.toString(),
            product_name: p.product,
            description: p.description,
            price: (p.isChild && p.pricing <= 0) ? 0 : p.pricing,
            quantity: p.quantity,
            discount: (p.isChild && p.pricing <= 0) ? 0 : p.discount,
            option_index: 1,
            line_item_index: p.line_item_index,
            remarks: p.remarks,
            item_hidden: p.item_hidden || false
          })
        })

        // proposal_section_content is no longer sent — the backend auto-generates
        // it from default blocks and lead data in BuildProposalSectionContent()

        const response = await this.$axios.post("/leads/" + this.lead.id + "/vertex-proposals", leadProposal);
        if (response.data.success) {
          this.$toast.success(response.data.message)
          this.$router.push({ name: 'proposal-view', params: { id: response.data.vertex_proposal.id } })
        } else {
          this.$toast.error(response.data.message)
          console.error("457")
          this.isLoading = false
        }
      } catch (error) {
        this.$toast.error(error.response?.data?.message || error.message || "Failed to create proposal.")
        this.isLoading = false
      }
    },

   extractSectionFromHTML(htmlString, elementId) {
      if (!htmlString) return "<p><br></p>"; // Failsafe if the block is empty
      
      // Create a virtual DOM from the string
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");
      
      // Query the virtual DOM
      const el = doc.getElementById(elementId);
      
      return el ? el.innerHTML : "<p><br></p>";
    },

    startEdit(index, field) {
      this.productList.forEach((item, i) => {
        if (i === index) {
          item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
        } else {
          item.isEditingProduct = false;
          item.isEditingDescription = false;
          item.isEditingRemarks = false;
          item.isEditingQuantity = false;
          item.isEditingPricing = false;
          item.isEditingDiscount = false;
        }
      });
    },
    endEdit(item, field) {
      item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
      this.updateProductConcat();
    },

    // Sync child quantity accurately on user input
    syncChildQuantities(parentItem) {
      if (!parentItem.isChild) {
        this.productList.forEach(item => {
          if (item.isChild && item.uiParentId === parentItem.uiParentId) {
            item.quantity = parentItem.quantity;
          }
        });
      }
    },

    async getLead(id) {
      if (isEmpty(id)) return

      await this.$axios.get("/leads/" + id)
        .then((response) => {
          this.lead = response.data.lead
          this.productList = []
          this.isCharityLead = response.data.lead.is_charity
          this.convertLeadProductsToSelectedProducts(response.data.lead.products, this.isCharityLead)
        })
        .catch((error) => {
          this.$toast.error(error)
        })
    },

    selectProduct(selectedProduct) {
      const isCharityFree = selectedProduct.isCharityProduct && this.isCharityLead;

      const pricingToUse = isCharityFree ? 0 : selectedProduct.defaultPricing;
      const discountToUse = isCharityFree ? 0 : (selectedProduct.defaultDiscount || 0.00);

      const uiParentId = Date.now() + Math.random(); // Unique trace for this instance

      // 1. Add Parent Product
      const newItem = {
        id: selectedProduct.id,
        product: selectedProduct.name,
        description: this.convertToHtml(selectedProduct.description || "No Product Description"),
        remarks: selectedProduct.remarks || "",
        quantity: selectedProduct.defaultQuantity || 1,
        discount: discountToUse,
        pricing: pricingToUse,
        planType: selectedProduct.pricingPlanType || "",
        line_item_index: ++this.line_item_index,
        isChild: selectedProduct.parentIds && selectedProduct.parentIds.length > 0,
        uiParentId: uiParentId,
        item_hidden: false,
        classification: 'Client',
        is_test_data: false,
        bdm_id: this.leadForm.bdm_id ? parseInt(this.leadForm.bdm_id) : 0
      };

      this.productList.push(newItem);

      // 2. Add Children Products Automatically (If any)
      if (!selectedProduct.parentIds || selectedProduct.parentIds.length === 0) {
        const children = this.allMappedProducts.filter(p => p.parentIds && p.parentIds.includes(selectedProduct.id));
        children.forEach(childProd => {
          const childPrice = childProd.defaultPricing || 0.00;
          this.productList.push({
            id: childProd.id,
            product: childProd.name,
            description: this.convertToHtml(childProd.description || "No Product Description"),
            remarks: childProd.remarks || "",
            quantity: selectedProduct.defaultQuantity || 1, // Sync with parent default
            discount: 0, // Enforce 0 for children calculation
            pricing: childPrice, // Enforce 0 for children calculation
            line_item_index: ++this.line_item_index,
            isChild: true,
            uiParentId: uiParentId, // Links this child to the parent above
            item_hidden: false,
            planType: childProd.pricingPlanType || "",
            defaultPricing: childPrice
          });
        });
      }

      this.isSelectingProduct = false;
      this.newProductIndex = -1;
      this.updateProductConcat();
    },

    removeProduct(indexToRemove) {
      if (indexToRemove > -1) {
        const item = this.productList[indexToRemove];

        if (!item.isChild) {
          // If it's a parent, remove the parent AND all its associated children
          const parentUiId = item.uiParentId;
          this.productList = this.productList.filter(p => p.uiParentId !== parentUiId);
        } else {
          // If it's a child, remove only the child
          this.productList.splice(indexToRemove, 1);
        }

        this.updateProductConcat();
      }
    },

    updateProductConcat() {
      this.product_concat = this.productList.map(item => item.product).join(' & ');

      if (isEmpty(this.product_concat)) {
        this.product_concat = "No Product(s)"
      }
    },

    // buildPricingTable() {
    //   let productsToPropagate = ""

    //   this.productList.forEach(p => {
    //     // Completely skip rendering this item if it is hidden
    //     if (p.item_hidden) return;

    //     let productCell = p.isChild
    //       ? `<span style="padding-left: 20px; color: #666;">&#8627; ${p.product}</span>`
    //       : `<b>${p.product}</b>`;

    //     // Hide pricing text entirely for children in the rendered HTML table
    //     let priceCell = p.isChild ? "" : this.formatCurrency(p.pricing);
    //     let discountCell = p.isChild ? "" : this.formatCurrency(p.discount);
    //     let totalDiscountCell = p.isChild ? "" : this.formatCurrency(this.calculateTotalDiscount(p));
    //     let totalCell = p.isChild ? "" : this.formatCurrency(this.calculateTotal(p));

    //     productsToPropagate +=
    //       `<tr id='product_list_${p.id}'>
    //       <td style="border-color: black; border-width: 1px; text-align: left; padding-left: 8px;">${productCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.description || "No Product Description"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.remarks || "No Remarks"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.quantity}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${priceCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${discountCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${totalDiscountCell}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${totalCell}</td>
    //     </tr>
    //     `})

    //   productsToPropagate += `
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;"><b>Subtotal<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateSubTotalPrice)}</td>
    //   </tr>
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td title="Goods and Services Tax" style="border-color: black; border-width: 1px; text-align: center;"><b>GST (10%)<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalGST)}</td>
    //   </tr>
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //        <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;"><b>Total<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalPrice)}</td>
    //   </tr>
    //     `

    //   const mContent = this.BlockTypeContents.find(btc => btc.type == "PricingTable")
    //   mContent.content = ` <table style="border-collapse: collapse; width: 100%; border-color: black; border: 2px solid black;" border="1">
    //         <thead>
    //         <tr>
    //           <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: left; padding-left: 8px;">Product</th>
    //           <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Description</th>
    //           <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Notes</th>
    //           <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Qty</th>
    //           <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Price</th>
    //           <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Per Item Discount</th>
    //           <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total Discount</th>
    //           <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total</th>
    //         </tr>
    //         <thead>
    //     <tbody>
    //            ${productsToPropagate}
    //     </tbody>
    //   </table>`
    // },

    replaceVariableFields() {
      // Resolve the BDM name for relations_manager from the logged-in user (the proposal creator)
      let bdmName = 'Martin Boyd'; // Default fallback
      const currentUser = this.$store.state.auth.currentUser;
      if (currentUser && (currentUser.first_name || currentUser.last_name)) {
        bdmName = ((currentUser.first_name || '') + ' ' + (currentUser.last_name || '')).trim();
      }

      const performReplacement = (content) => {
        if (isEmpty(content)) return content;
        let replacedContent = content;

        this.variableFieldsMapping.forEach(vfm => {
          let variableId = vfm.replace_variable;
          if (variableId === 'phone_number') variableId = 'contact_number';
          if (variableId === 'email') variableId = 'billing_email';

          let val;
          if (vfm.replace_variable === 'relations_manager') {
            val = bdmName;
          } else if (vfm.replace_variable === 'business_number') {
            val = this.lead ? (this.lead.business_number || this.lead.abn) : '';
          } else if (vfm.replace_variable === 'business_number_type') {
            val = this.lead ? (this.lead.business_number_type || (isAustralianCountry(this.lead.country) ? 'ABN' : 'Business Number')) : 'Business Number';
          } else if (vfm.replace_variable === 'business_number_formatted') {
            const bNum = this.lead ? (this.lead.business_number || this.lead.abn) : '';
            const bType = this.lead ? (this.lead.business_number_type || (isAustralianCountry(this.lead.country) ? 'ABN' : 'Business Number')) : 'Business Number';
            val = bNum ? `${bType}: ${bNum}` : '';
          } else {
            val = this.lead ? this.lead[vfm.replace_variable] : '';
          }

          let fillText = "<b>{No Record}</b>";
          if (!isEmpty(val)) {
             fillText = "<b>" + val + "</b>";
          } else if (vfm.replace_variable === 'abn' || vfm.replace_variable === 'business_number') {
             fillText = "<b>N/A</b>";
          } else if (vfm.replace_variable === 'org_address') {
             fillText = " ";
          }

          // Escape variableId for use in regex
          let safeVarId = variableId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          
          // Primary regex: try to replace the entire <mark> block if it wraps just this variable cleanly
          let rgxMark = new RegExp(`<mark[^>]*>\\s*\\{\\{\\s*<span[^>]*>!\\*<\\/span>\\s*<span id="variable_value_${safeVarId}"[^>]*>.*?<\\/span>\\s*<span[^>]*>\\*!<\\/span>\\s*\\}\\}\\s*<\\/mark>`, 'gi');
          
          // Fallback regex: replaces just the inner {{ ... }} if nested inside other marks
          let rgxFallback = new RegExp(`\\{\\{[^{}]*id="variable_value_${safeVarId}"[^{}]*\\}\\}`, 'gi');

          if (rgxMark.test(replacedContent)) {
             replacedContent = replacedContent.replace(rgxMark, fillText);
          } else {
             replacedContent = replacedContent.replace(rgxFallback, fillText);
          }
        });

        return replacedContent;
      };

      this.BlockTypeContents.forEach(btc => {
        if (this.lead != undefined && !isEmpty(btc.content)) {
          btc.content = performReplacement(btc.content);
        }
      });

      this.ContentTypeContents.forEach(ctc => {
        if (this.lead != undefined && !isEmpty(ctc.content)) {
          ctc.content = performReplacement(ctc.content);
        }
      });
    },

    buildContent() {
      // ${this.getDefaultContent("PricingTable") || "<h3 style='color: #435666; margin: 0;'>[Automated Pricing Table Placeholder]</h3>"}
      this.replaceVariableFields()
      let sections = `
        <section id='cover_page_section' class='non-editable-section' contenteditable="false" style="user-select: none; margin-bottom: 20px;"> 
          <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='cover_section_label' disabled>
            Cover Section
          </p>
          <div id='start_cover_section' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
            ${this.getDefaultContent("CoverPage") || "<p><br></p>"}
          </div>
        </section>
        
        <hr style="border-top: 1px solid #eee; margin: 20px 0;">

        <section id='product_services_section' class='non-editable-section' contenteditable="false" style="user-select: none; margin-bottom: 20px;">
          <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='product_services_section_label' disabled>
            Product & Services Section
          </p> 
          <div id='start_product_services_section' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
            ${this.getDefaultContentProductService(this.productList) || "<p><br></p>"}
          </div>
        </section>
        
        <hr style="border-top: 1px solid #eee; margin: 20px 0;">  

        <section id='pricing_table_section' class='non-editable-section' contenteditable="false" style="user-select: none; margin-bottom: 20px;">
          <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='pricing_table_section_label'>
            Pricing Table Section
          </p>  
          
          <div id='start_pricing_table_intro' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none; margin-bottom: 15px;">
             <p><em>[Enter your introductory text for the pricing table here...]</em></p>
          </div>
 
          <div id='start_pricing_table_main' class='non-editable-section' contenteditable="false" style="background-color: #f9f9f9; padding: 20px; text-align: center; border: 1px solid #ddd; margin-bottom: 15px;">
           
          </div>
 
          <div id='start_pricing_table_notes' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
             <p><em>[Enter any concluding remarks or notes below the pricing table here...]</em></p>
          </div>
        </section>
        
        <hr style="border-top: 1px solid #eee; margin: 20px 0;">

        <section id='image_grid_section' class='non-editable-section' contenteditable="false" style="user-select: none; margin-bottom: 20px;">
          <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='image_grid_section_label'>
            Image Grid Section
          </p>  
          <div id='start_image_grid_section' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
            ${this.getDefaultContent("Image") || "<p><br></p>"}
          </div>  
        </section>
        
        <hr style="border-top: 1px solid #eee; margin: 20px 0;">

        <section id='content_social_impact_section' class='non-editable-section' contenteditable="false" style="user-select: none; margin-bottom: 20px;">
          <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='social_impact_section_label'>
            Social Impact Section
          </p>  
          <div id='start_content_social_impact_section' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
            ${this.getDefaultContentTypeContent("SocialImpact") || "<p><br></p>"}
          </div>  
        </section>
        
        <hr style="border-top: 1px solid #eee; margin: 20px 0;">

        <section id='content_commitment_section' class='non-editable-section' contenteditable="false" style="user-select: none; margin-bottom: 20px;">
          <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='commitment_section_label'>
            Insurance Section
          </p>  
          <div id='start_content_commitment_section' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
            ${this.getDefaultContentTypeContent("CommitmentToSafetyAndCompliance") || "<p><br></p>"}
          </div>  
        </section>
        
        <hr style="border-top: 1px solid #eee; margin: 20px 0;">

        <section id='content_terms_condition_section' class='non-editable-section' contenteditable="false" style="user-select: none; margin-bottom: 20px;">
          <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='terms_condition_section_label'>
            Terms & Conditions Section
          </p>  
          <div id='start_content_terms_condition_section' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
            ${this.getDefaultContentTypeContent("TermsAndConditions") || "<p><br></p>"}
          </div>  
        </section>
        
        <hr style="border-top: 1px solid #eee; margin: 20px 0;">

        <section id='content_next_steps_section' class='non-editable-section' contenteditable="false" style="user-select: none; margin-bottom: 20px;">
          <p style='text-align:center; color: #00A0C1; font-weight: bold; margin-bottom: 5px; cursor: default;' id='next_steps_section_label'>
            Next Steps
          </p>  
          <div id='start_content_next_steps_section' class='editable-content-inside' contenteditable="true" style="user-select: text; min-height: 50px; padding: 10px; border: 1px dashed #ccc; background-color: #fff; display: block; outline: none;">
            ${this.getDefaultContentTypeContent("NextSteps") || "<p><br></p>"}
          </div>  
        </section>
      `
      return sections
    },

    getVertexAuthUser(id) {
      if (this.$store.state.auth.currentUser && (this.$store.state.auth.currentUser.id === id || !id)) {
        const cu = this.$store.state.auth.currentUser;
        const fullName = `${cu.first_name || ''} ${cu.last_name || ''}`.trim();
        if (fullName) return fullName;
        if (cu.email) return cu.email;
      }
      if (Array.isArray(this.vtxUsers)) {
        let user = this.vtxUsers.find(user => user && user.id === id);
        if (user) {
          return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || "Deleted User";
        }
      }
      if (id != null && id !== "") {
        return "Deleted User"
      } else {
        return "System Generated"
      }
    },

    getVertexUsers() {
      this.vtxUsers = this.$store.state.auth.usersWithVertexRoles || []
    },
  },
});
</script>
<style scoped>
.container {
  background-color: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 100%;
}

.two-column-layout {
  display: flex;
  gap: 20px;
}

.left-column {
  flex: 2;
  overflow-x: auto;
}

.right-column {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.proposal-header {
  text-align: center;
  margin-bottom: 20px;
}

.proposal-name-label {
  color: #777;
  font-size: 0.9em;
  margin-bottom: 5px;
}

.proposal-name {
  background-color: #e9ecef;
  padding: 10px;
  border-radius: 5px;
  font-weight: bold;
  color: #333;
}

.product-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  border: 1px solid #ddd;
}

.product-table th,
.product-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.product-table th {
  background-color: #00bcd4;
  color: white;
  font-weight: bold;
}

.product-table tr:last-child td {
  border-bottom: none;
}

.add-product-row td {
  color: #00bcd4;
  cursor: pointer;
}

.add-product-row td:first-child {
  font-weight: bold;
}

.button {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
}

.save-draft {
  background-color: #fff;
  color: #ff9800;
  border: 1px solid #ff9800;
}

/* Custom Vertex Text Color */
.text-vertex-blue {
  color: #00A0C1 !important;
}

/* Subtle remove icon next to the product name */
.remove-icon {
  color: #dc3545;
  cursor: pointer;
  font-size: 1.1em;
  transition: transform 0.2s, color 0.2s;
}

.remove-icon:hover {
  color: #a71d2a;
  transform: scale(1.2);
}

.continue {
  background-color: #00bcd4;
  color: white;
}

.arrow {
  font-size: 0.8em;
  color: #777;
}

.product-selection-list {
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  padding: 10px;
  margin-top: 5px;
  border-radius: 5px;
}

.product-item {
  padding: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.product-item:hover {
  background-color: #eee;
}

.product-table td input[type="number"] {
  width: 70px;
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9em;
  text-align: left;
}

.product-table td input[type="number"]:focus {
  outline: none;
  border-color: #00bcd4;
  box-shadow: 0 0 5px rgba(0, 188, 212, 0.5);
}

.product-selection-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.product-selection-table th,
.product-selection-table td {
  padding: 8px;
  border: 1px solid #ddd;
  text-align: left;
}

.product-selection-table th {
  background-color: #f2f2f2;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 1;
}

.product-item {
  padding: 0;
  margin: 0;
  border: none;
  cursor: pointer;
}

.pricing-summary-footer {
  background: linear-gradient(180deg, rgba(0, 188, 212, 0.02) 0%, rgba(0, 188, 212, 0.08) 100%);
  border-top: 2px solid #00bcd4;
}
.pricing-summary-footer td {
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(0, 188, 212, 0.1) !important;
}
.pricing-summary-footer .summary-label {
  text-align: right;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #6c757d;
}
.pricing-summary-footer .summary-value {
  text-align: right;
  font-weight: 700;
  font-size: 1.05rem;
  color: #333;
}
.pricing-summary-footer .total-row td {
  border-bottom: none !important;
  padding-top: 14px;
  padding-bottom: 14px;
}
.pricing-summary-footer .total-label {
  color: #212529;
  font-size: 1.1rem;
  text-transform: uppercase;
}
.pricing-summary-footer .total-value {
  color: #00bcd4;
  font-size: 1.25rem;
}

.product-button-container {
  display: flex;
  margin-left: -3px;
  gap: 3px;
}

.red {
  border: 1px solid red;
  color: red;
}

.red:hover {
  border: 1px solid red;
  color: white;
  background-color: red;
}

.red:focus {
  border: 1px solid red;
  color: red;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

.styled-checkbox {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #00bcd4;
  border-radius: 4px;
  margin-right: 12px;
  display: inline-block;
  position: relative;
  transition: background-color 0.2s, border-color 0.2s;
}

.checkbox-custom::after {
  content: '';
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

.styled-checkbox:checked+.checkbox-custom {
  background-color: #00bcd4;
  border-color: #00bcd4;
}

.styled-checkbox:checked+.checkbox-custom::after {
  display: block;
}

.button-select {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
}

.add-btn-small {
  padding: 6px 12px;
  font-size: 0.9em;
}

.char-counter {
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 2px;
}

.remarks-input {
  width: 100%;
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9em;
}

.product-search-input {
  width: 100%;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.product-count-bar {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.product-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #00A0C1, #00bcd4);
  color: white;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.3);
}

.product-count-badge i {
  font-size: 0.8rem;
}

.available-count-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 8px;
  padding: 0 2px;
}

.available-count-label i {
  font-size: 0.7rem;
  color: #00bcd4;
}

/* Rich Text Modal Editor Styles */
.rich-text-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(5px);
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1060;
    display: flex;
    align-items: center;
    justify-content: center;
}
.rich-text-modal {
    background: #fff;
    border-radius: 12px;
    width: 600px;
    max-width: 90vw;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: fadeIn 0.2s ease-out;
}
.rich-text-modal-header {
    background-color: #00A0C1;
    color: white;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.rich-text-modal-header h5 {
    margin: 0;
    font-weight: 600;
    font-size: 1.15rem;
}
.btn-close-custom {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
    opacity: 0.8;
    transition: opacity 0.2s;
}
.btn-close-custom:hover {
    opacity: 1;
}
.rich-text-modal-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.rich-text-toolbar {
    display: flex;
    gap: 6px;
    background: #f1f3f5;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}
.toolbar-btn {
    background: white;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 0.9rem;
    cursor: pointer;
    color: #495057;
    transition: all 0.2s;
}
.toolbar-btn:hover {
    background: #e9ecef;
    border-color: #adb5bd;
    color: #212529;
}
.rich-text-editor {
    border: 1px solid #ced4da;
    border-radius: 8px;
    padding: 12px;
    min-height: 150px;
    max-height: 300px;
    overflow-y: auto;
    font-size: 0.95rem;
    line-height: 1.6;
    color: #333;
    outline: none;
    background: white;
    text-align: left;
}
.rich-text-editor:focus {
    border-color: #00A0C1;
    box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}
.rich-text-editor ul {
    margin-top: 0;
    margin-bottom: 8px;
    padding-left: 20px;
    list-style-type: disc !important;
}
.rich-text-editor li {
    margin-bottom: 4px;
}
.rich-text-char-counter {
    text-align: right;
    font-size: 0.8rem;
    color: #6c757d;
}
.rich-text-modal-footer {
    padding: 16px 20px;
    background: #f8f9fa;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}
.btn-cancel {
    background: white;
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 8px 16px;
    font-weight: 600;
    cursor: pointer;
    color: #495057;
    transition: all 0.2s;
}
.btn-cancel:hover {
    background: #f1f3f5;
    border-color: #adb5bd;
}
.btn-save {
    background: #00bcd4;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 20px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
}
.btn-save:hover {
    background: #00a5bb;
}
@keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
}

.high, ::v-deep(.high) {
   color: #EB1C24;
}

.low, ::v-deep(.low) {
   color: #00A54F;
}

.medium, ::v-deep(.medium) {
   color: #FFB300;
}

::v-deep(.multiselect__option--highlight) .high,
::v-deep(.multiselect__option--highlight) .low,
::v-deep(.multiselect__option--highlight) .medium {
   color: #ffffff !important;
}

::v-deep(.inline-bdm-multiselect) {
  flex: 1;
  min-width: 220px;
}
::v-deep(.inline-bdm-multiselect .multiselect__tags) {
  min-height: 36px !important;
  height: 36px !important;
  padding: 0 40px 0 12px !important;
  border-radius: 8px !important;
  border: 1px solid #fde68a !important;
  background-color: #fff !important;
  display: flex !important;
  align-items: center !important;
}
::v-deep(.inline-bdm-multiselect .multiselect__select) {
  height: 34px !important;
  width: 38px !important;
  top: 1px !important;
}
::v-deep(.inline-bdm-multiselect .multiselect__placeholder) {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  font-size: 13px !important;
  color: #94a3b8 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  width: 100% !important;
}
::v-deep(.inline-bdm-multiselect .multiselect__single) {
  margin-bottom: 0 !important;
  padding-left: 0 !important;
  font-size: 13px !important;
  color: #333 !important;
  background: transparent !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  width: 100% !important;
  max-width: calc(100% - 10px) !important;
  display: block !important;
}
::v-deep(.inline-bdm-multiselect .multiselect__input) {
  margin-bottom: 0 !important;
  padding-left: 0 !important;
  font-size: 13px !important;
  background: transparent !important;
  white-space: nowrap !important;
}
::v-deep(.inline-bdm-multiselect .multiselect__content-wrapper) {
  border-radius: 8px !important;
  border-color: #00A0C1 !important;
  font-size: 13px !important;
  box-shadow: 0 4px 12px rgba(0, 160, 193, 0.1) !important;
}
::v-deep(.inline-bdm-multiselect .multiselect__option--highlight) {
  background: #00A0C1 !important;
  color: white !important;
}

/* Contact Type Badges */
.contact-badge-item {
  display: inline-flex !important;
  align-items: center !important;
  line-height: 1.2 !important;
  font-weight: 700 !important;
  letter-spacing: 0.4px !important;
  text-transform: uppercase !important;
  transition: all 0.15s ease-in-out !important;
}

.contact-type-badge-lead {
  background-color: rgba(14, 165, 233, 0.12) !important;
  color: #0284c7 !important;
  border: 1px solid rgba(14, 165, 233, 0.3) !important;
}

.contact-type-badge-primary {
  background-color: rgba(16, 185, 129, 0.12) !important;
  color: #059669 !important;
  border: 1px solid rgba(16, 185, 129, 0.3) !important;
}

.contact-type-badge-technical {
  background-color: rgba(124, 58, 237, 0.12) !important;
  color: #7c3aed !important;
  border: 1px solid rgba(124, 58, 237, 0.3) !important;
}

.contact-type-badge-billing {
  background-color: rgba(217, 119, 6, 0.12) !important;
  color: #b45309 !important;
  border: 1px solid rgba(217, 119, 6, 0.3) !important;
}

.contact-type-badge-signer {
  background-color: rgba(225, 29, 72, 0.12) !important;
  color: #e11d48 !important;
  border: 1px solid rgba(225, 29, 72, 0.3) !important;
}

.contact-type-badge-user {
  background-color: rgba(100, 116, 139, 0.12) !important;
  color: #475569 !important;
  border: 1px solid rgba(100, 116, 139, 0.3) !important;
}

.contact-type-badge-default {
  background-color: rgba(107, 114, 128, 0.1) !important;
  color: #4b5563 !important;
  border: 1px solid rgba(107, 114, 128, 0.25) !important;
}

::v-deep(.contact-multiselect .multiselect__option--highlight) {
  background: #00A0C1 !important;
}

::v-deep(.contact-multiselect .multiselect__option--highlight .contact-option-name) {
  color: #ffffff !important;
}

::v-deep(.contact-multiselect .multiselect__option--highlight .contact-option-sub) {
  color: rgba(255, 255, 255, 0.85) !important;
}

::v-deep(.contact-multiselect .multiselect__option--highlight .contact-badge-item) {
  background-color: rgba(255, 255, 255, 0.22) !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.45) !important;
}

::v-deep(.contact-multiselect .multiselect__option::after),
::v-deep(.contact-multiselect .multiselect__option:after) {
  display: none !important;
  content: "" !important;
  width: 0 !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

::v-deep(.contact-multiselect .multiselect__option) {
  padding: 10px 14px !important;
  min-height: 42px !important;
  display: flex !important;
  align-items: center !important;
}

::v-deep(.contact-multiselect .multiselect__content) {
  width: 100% !important;
}

::v-deep(.contact-multiselect .multiselect__content-wrapper) {
  overflow-x: hidden !important;
}

/* Client Information Preview Card Modern Styling */
.lead-preview-card {
  background: #f8fafc !important;
  border-radius: 10px !important;
  border: 1px solid #e2e8f0 !important;
  color: #334155 !important;
}

.client-info-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: #64748b;
  margin-bottom: 4px;
}

.client-info-value {
  font-size: 13.5px;
  line-height: 1.4;
  color: #1e293b;
}

.product-name-cell {
  cursor: pointer;
}

.product-name-input {
  width: 100%;
  min-width: 120px;
  padding: 4px 8px;
  border: 1px solid #00bcd4;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: bold;
  outline: none;
  box-shadow: 0 0 5px rgba(0, 188, 212, 0.4);
}
</style>